import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Screenshot } from "../../../assets/icons/svg/screenshot.svg";
import { ReactComponent as Video } from "../../../assets/icons/svg/video.svg";
import { ReactComponent as Share } from "../../../assets/icons/svg/Share.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/icons/svg/Back-arrow.svg";
import { ReactComponent as Filter } from "../../../assets/icons/svg/Filter.svg";
import { ReactComponent as Search } from "../../../assets/icons/svg/Search.svg";
import { ReactComponent as Edit } from "../../../assets/icons/svg/Edit.svg";
import { ReactComponent as Play } from "../../../assets/icons/svg/play-icon.svg";
import { ReactComponent as Pause } from "../../../assets/icons/svg/pause-icon.svg";
import { ReactComponent as SkipBackward } from "../../../assets/icons/svg/5-sec-backward.svg";
import { ReactComponent as SkipForward } from "../../../assets/icons/svg/5-sec-forward.svg";
import { ReactComponent as VideoPlay } from "../../../assets/icons/svg/VIDEO-PLAY.svg";
import { ReactComponent as VideoPause } from "../../../assets/icons/svg/VIDEO-PAUSE.svg";
import { ReactComponent as Audio } from "../../../assets/icons/svg/audio.svg";
import { ReactComponent as PlayRecording } from "../../../assets/icons/svg/play-recording.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as UnclearIcon } from "../../../assets/icons/svg/unclear_icon.svg";
import { ReactComponent as UnclearUpIcon } from "../../../assets/icons/svg/unclear-up-icon.svg";
import { ReactComponent as UnclearDownIcon } from "../../../assets/icons/svg/unclear-drop-icon.svg";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import Lottie from "lottie-react";
import DotLoader from "../../../assets/Animation/Dot Loader.json";
import { useProfileImage } from "../../../contexts/ProfileImageContext";

import {
  Camera,
  X,
  Check,
  X as XIcon,
  Volume2,
  VolumeX,
  MoreVertical,
  Trash2,
} from "lucide-react";
import TextEditor from "../../ui/TextEditor";
import VoiceNoteRecorder from "../../ui/screenshot-screenrecord/Screenshot/VoiceNoteRecorder";
import AudioPlayer from "../../ui/AudioPlayer";
import ShareFeedbackPopup from "../../ui/ShareFeedbackPopup";
import Button from "../../ui/buttons";
import { shareFeedbackWithDeveloper } from "../../../api/api-utils";
import { API_URLS, BASE_URL } from "../../../api/urls";
import ScreenshotPreviewComponent, {
  ScreenshotSubmission,
} from "../../ui/screenshot-screenrecord/Screenshot/Screenshot_Preview";
import ScreenRecordingPreview, {
  RecordingSubmission,
} from "../../ui/screenshot-screenrecord/screen-recording/ScreenRecordingPreview";
import DeveloperAvatarsGroup from "../../ui/DeveloperAvatarsGroup";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import UploadQueueManager from "../../../utils/uploadQueueManager";
import UploadQueueIndicator from "../../ui/UploadQueueIndicator";
import {
  FeedbackItem,
  ViewListFeedbackProps,
  DeveloperStatus,
} from "../../../types/feedback.types";
import {
  stripHtmlTags,
  createVideoUrl,
  isValidVideoSource,
  AudioConverter,
} from "../../../utils/feedbackUtils";

declare global {
  interface Window {
    __unclearDetailsCache?: Record<string, any>;
  }
}

// Token Manager for API calls (keep existing implementation)
class TokenManager {
  private static ACCESS_TOKEN_KEY = "access_token";

  static getAccessToken(): string | null {
    try {
      return (
        localStorage.getItem(this.ACCESS_TOKEN_KEY) ||
        (window as any).__feebdack_tokens?.access_token ||
        null
      );
    } catch (error) {
      console.warn("Could not retrieve access token:", error);
      return (window as any).__feebdack_tokens?.access_token || null;
    }
  }
}

// Voice Upload Manager with Fixed Feedback ID Handling
class VoiceUploadManager {
  static async uploadVoiceRecording(
    feedbackId: string,
    audioBlob: Blob,
    filename: string,
  ): Promise<{ success: boolean; error?: string; voice_url?: string }> {
    try {
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        throw new Error("No access token available");
      }

      // Extract numeric ID from feedback ID string (e.g., "feedback_590" -> "590")
      const numericFeedbackId = String(feedbackId)
        .replace(/^feedback_/, "")
        .trim();

      // Validate that we have a valid numeric ID
      if (!/^\d+$/.test(numericFeedbackId)) {
        throw new Error(
          `Invalid feedback ID format: ${feedbackId}. Expected format: "feedback_123" or "123"`,
        );
      }

      console.log("🎵 Starting voice upload process:", {
        originalFeedbackId: feedbackId,
        numericFeedbackId: numericFeedbackId,
        originalSize: audioBlob.size,
        originalType: audioBlob.type,
        filename,
      });

      let processedAudioFile = audioBlob;
      let processedFileName = filename;

      console.log("🎵 Converting audio to MP3 for API compatibility...");
      try {
        processedAudioFile = await AudioConverter.convertToMP3(audioBlob);
        processedFileName = filename.replace(/\.(wav|webm|ogg|mp4)$/, ".mp3");
        console.log("🎵 Audio conversion successful:", {
          originalSize: audioBlob.size,
          convertedSize: processedAudioFile.size,
          newFileName: processedFileName,
        });
      } catch (conversionError) {
        console.warn(
          "🎵 Audio conversion failed, using fallback:",
          conversionError,
        );
        processedAudioFile = new Blob([audioBlob], { type: "audio/mp3" });
        processedFileName = filename.replace(/\.(wav|webm|ogg|mp4)$/, ".mp3");
      }

      const formData = new FormData();

      if (
        processedAudioFile instanceof Blob &&
        !(processedAudioFile instanceof File)
      ) {
        const fileObject = new File([processedAudioFile], processedFileName, {
          type: processedAudioFile.type || "audio/mp3",
          lastModified: Date.now(),
        });
        formData.append("file", fileObject);
      } else {
        formData.append("file", processedAudioFile, processedFileName);
      }

      // FIXED: Send only the numeric ID to the API
      formData.append("feedback_id", numericFeedbackId);
      formData.append("file_type", "audio");

      console.log("🎵 FormData contents:");
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        if (value instanceof File) {
          console.log(
            `  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`,
          );
        } else {
          console.log(`  ${key}: ${value}`);
        }
      });

      const response = await fetch(API_URLS.UPLOAD_FILE_VOICE, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      console.log("🎵 Response status:", response.status);

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorText = await response.text();
          console.error("🎵 Error response body:", errorText);
          if (errorText) {
            errorMessage = `HTTP ${response.status}: ${errorText}`;
          }
        } catch (e) {
          console.error("🎵 Could not read error response:", e);
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("✅ Voice note uploaded successfully:", result);

      return {
        success: true,
        voice_url: result.voice_url,
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      console.error("❌ Voice upload failed:", error);
      return { success: false, error: errorMessage };
    }
  }
}

// Audio checking function (keep existing)
const checkAudioExists = async (feedbackId: string): Promise<boolean> => {
  try {
    const accessToken = TokenManager.getAccessToken();
    if (!accessToken) return false;

    const apiUrl = API_URLS.GET_FILE_VOICE(feedbackId);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Range: "bytes=0-1",
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const exists = response.ok || response.status === 206;

      console.log(
        `🎵 Audio check for ${feedbackId}: ${
          exists ? "EXISTS" : "NOT_FOUND"
        } (${response.status})`,
      );
      return exists;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === "AbortError") {
        console.warn(`🎵 Audio check timeout for ${feedbackId}`);
        return false;
      }

      try {
        const headResponse = await fetch(apiUrl, {
          method: "HEAD",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        return headResponse.ok;
      } catch {
        console.error(`🎵 Audio check failed for ${feedbackId}:`, error);
        return false;
      }
    }
  } catch (error) {
    console.error("Failed to check audio existence:", error);
    return false;
  }
};

const shareCallTracker = new Map<number, boolean>();

// Main Component
const ViewListFeedback: React.FC<ViewListFeedbackProps> = ({
  feedbackName,
  totalFeedbacks,
  onBack,
  onShareWithDeveloper,
  feedbackItems = [],
  onUpdateFeedback,
  onUpdateAudio,
  onDeleteFeedback,
  cardId: propCardId,
  workspaceId: propWorkspaceId,
  isSubmittedMode: propIsSubmittedMode = false, // ✅ NEW: Default to false
  showDeveloperStatus = true, // ✅ NEW: Add this prop with default true
  showBackButton = false,
  onContinueEditing,
  onGoBackToDraft,
}) => {
  const navigate = useNavigate();

  //  Get profile image from context
  const { profileImage } = useProfileImage();

  // State (keep existing state declarations)
  const [localFeedbackItems, setLocalFeedbackItems] =
    useState<FeedbackItem[]>(feedbackItems);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    feedbackItems.length > 0 ? feedbackItems[0].id : null,
  );

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<
    | "all"
    | "screenshot"
    | "recording"
    | "acknowledged"
    | "unclear"
    | "reshare"
    | "not_read"
  >("all");
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editedFeedbackText, setEditedFeedbackText] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // NEW STATE: Submit feedback confirmation
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmittedMode, setIsSubmittedMode] = useState(false); // ✅ NEW

  // Video player state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoVolume, setVideoVolume] = useState(1);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Audio state management
  const [audioExistsMap, setAudioExistsMap] = useState<
    Record<string, boolean | "loading">
  >({});

  const [forceRecorderMode, setForceRecorderMode] = useState<
    Record<string, boolean>
  >({});

  const [isUploadingAudio, setIsUploadingAudio] = useState<
    Record<string, boolean>
  >({});
  const [uploadError, setUploadError] = useState<Record<string, string>>({});

  const [voiceRecorderKey, setVoiceRecorderKey] = useState(0);

  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);

  const [cardId, setCardId] = useState<number | null>(null);
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(
    null,
  );
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousFeedback, setPreviousFeedback] = useState<FeedbackItem | null>(
    null,
  );

  const [feedbackStatuses, setFeedbackStatuses] = useState<
    Record<string, "acknowledged" | "unclear" | "reshare"> // ✅ CHANGED: Added "reshare"
  >({});
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [developerProfileImages, setDeveloperProfileImages] = useState<
    Record<string, string | null>
  >({});

  const [showAudioError, setShowAudioError] = useState(false);
  const [audioErrorMessage, setAudioErrorMessage] = useState("");

  const [showUnclearDrawer, setShowUnclearDrawer] = useState(false);
  const [unclearFeedbackData, setUnclearFeedbackData] = useState<{
    email: string;
    status: string;
    unclear_reason: string[];
    comment: string;
    action_time: string;
  } | null>(null);

  //  Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");

  const [textOverflowMap, setTextOverflowMap] = useState<
    Record<string, boolean>
  >({});
  const textContentRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  //  Overflow detection state
  const [isListOverflowing, setIsListOverflowing] = useState(false);

  const [hasSharedOnce, setHasSharedOnce] = useState(
    propIsSubmittedMode || false,
  );

  //  State for screenshot editing
  const [showEditScreenshotPreview, setShowEditScreenshotPreview] =
    useState(false);
  const [editingScreenshotData, setEditingScreenshotData] = useState<{
    dataUrl: string;
    feedbackId: string;
    feedback: string;
    audioUrl?: string;
    audioBlob?: Blob;
    audioFilename?: string;
  } | null>(null);

  // State for recording editing
  const [showEditRecordingPreview, setShowEditRecordingPreview] =
    useState(false);
  const [editingRecordingData, setEditingRecordingData] = useState<{
    dataUrl: string;
    feedbackId: string;
    feedback: string;
    recordingBlob?: Blob;
    duration: number;
    thumbnailUrl?: string;
  } | null>(null);

  const [drawerExpandedState, setDrawerExpandedState] = useState<
    Record<string, boolean>
  >({});
  //State for developer name tooltips
  const [hoveredDeveloperFeedback, setHoveredDeveloperFeedback] = useState<
    string | null
  >(null);
  const [developerNames, setDeveloperNames] = useState<Record<string, string>>(
    {},
  );

  const [shouldRefreshAfterUpload, setShouldRefreshAfterUpload] =
    useState(false);
  const [hasCheckedCompletedUploads, setHasCheckedCompletedUploads] =
    useState(false);

  // Refs (keep existing)
  const searchInputRef = useRef<HTMLInputElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const audioCheckCacheRef = useRef<Map<string, boolean>>(new Map());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const feedbackListRef = useRef<HTMLDivElement>(null);
  const feedbackItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const fetchedUnclearDetailsRef = useRef<Set<string>>(new Set());

  // Computed values (keep existing)
  const filteredFeedbackItems = useMemo(() => {
    return localFeedbackItems.filter((item) => {
      // Type filter
      let matchesTypeFilter = true;
      if (selectedFilter === "screenshot" || selectedFilter === "recording") {
        matchesTypeFilter = item.type === selectedFilter;
      }

      // Status filter (only applies when in submitted mode with developer status)
      let matchesStatusFilter = true;
      if (isSubmittedMode && showDeveloperStatus) {
        if (selectedFilter === "acknowledged") {
          matchesStatusFilter = item.uniqueFeedbackId
            ? feedbackStatuses[item.uniqueFeedbackId] === "acknowledged"
            : false;
        } else if (selectedFilter === "unclear") {
          matchesStatusFilter = item.uniqueFeedbackId
            ? feedbackStatuses[item.uniqueFeedbackId] === "unclear"
            : false;
        } else if (selectedFilter === "reshare") {
          // ✅ NEW FILTER LOGIC
          matchesStatusFilter = item.uniqueFeedbackId
            ? feedbackStatuses[item.uniqueFeedbackId] === "reshare"
            : false;
        } else if (selectedFilter === "not_read") {
          matchesStatusFilter = item.uniqueFeedbackId
            ? !feedbackStatuses[item.uniqueFeedbackId]
            : true;
        }
      } else if (
        selectedFilter === "acknowledged" ||
        selectedFilter === "unclear" ||
        selectedFilter === "reshare" || // ✅ ADDED
        selectedFilter === "not_read"
      ) {
        matchesStatusFilter = false;
      }

      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        (item.feedback || item.question || "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        item.type.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesTypeFilter && matchesStatusFilter && matchesSearch;
    });
  }, [
    localFeedbackItems,
    selectedFilter,
    searchQuery,
    isSubmittedMode,
    showDeveloperStatus,
    feedbackStatuses,
  ]);

  const selectedFeedback = useMemo(() => {
    return filteredFeedbackItems.find((item) => item.id === selectedFeedbackId);
  }, [filteredFeedbackItems, selectedFeedbackId]);

  const progressPercentage = useMemo(() => {
    if (!videoDuration || videoDuration <= 0 || isNaN(videoDuration)) {
      return 0;
    }
    if (isNaN(videoCurrentTime) || videoCurrentTime < 0) {
      return 0;
    }
    // Ensure we never exceed 100%
    return Math.max(0, Math.min(100, (videoCurrentTime / videoDuration) * 100));
  }, [videoCurrentTime, videoDuration]);

  const isMac = useMemo(() => {
    return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  }, []);

  // ✅ NEW: Listen for upload completions and refresh data
  useEffect(() => {
    const handleUploadCompleted = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { item, cardId: uploadCardId } = event.detail;

      console.log("📥 ViewListFeedback: Upload completed event received:", {
        itemId: item.id,
        itemType: item.type,
        uploadCardId,
        currentCardId: cardId,
      });

      // Only refresh if it's for the current card
      if (uploadCardId === cardId) {
        console.log("♻️ Triggering data refresh after upload completion");
        setShouldRefreshAfterUpload(true);
      }
    };

    const handleBulkUploadsCompleted = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { completed } = event.detail;

      // Check if any completed uploads are for our card
      const hasOurUploads = completed.some(
        (item: any) => item.cardId === cardId,
      );

      if (hasOurUploads) {
        console.log(
          "♻️ Multiple uploads completed for our card, refreshing...",
        );
        setShouldRefreshAfterUpload(true);
      }
    };

    window.addEventListener("upload-item-completed", handleUploadCompleted);
    window.addEventListener("uploads-completed", handleBulkUploadsCompleted);

    return () => {
      window.removeEventListener(
        "upload-item-completed",
        handleUploadCompleted,
      );
      window.removeEventListener(
        "uploads-completed",
        handleBulkUploadsCompleted,
      );
    };
  }, [cardId]);

  // Add effect to check for completed uploads on mount
  useEffect(() => {
    if (cardId && !hasCheckedCompletedUploads) {
      console.log("🔍 Checking for completed uploads on mount...");

      const completed = UploadQueueManager.getCompletedForCard(cardId);

      if (completed.length > 0) {
        console.log("✅ Found completed uploads, triggering refresh:", {
          count: completed.length,
          items: completed.map((c) => ({ id: c.id, type: c.type })),
        });

        setShouldRefreshAfterUpload(true);

        // Clean up completed items after triggering refresh
        setTimeout(() => {
          completed.forEach((item) => {
            UploadQueueManager.removeItem(item.id);
          });
          console.log("🧹 Cleaned up completed upload items");
        }, 1000);
      }

      setHasCheckedCompletedUploads(true);
    }
  }, [cardId, hasCheckedCompletedUploads]);

  useEffect(() => {
    // Check audio for all feedback items when they're first loaded
    const checkAllAudio = async () => {
      if (localFeedbackItems.length === 0) return;

      console.log("🎵 Checking audio for all feedback items on mount/update");

      for (const item of localFeedbackItems) {
        if (item.type === "screenshot" && item.uniqueFeedbackId) {
          const uniqueId = item.uniqueFeedbackId;

          // Skip if already checked
          if (audioCheckCacheRef.current.has(uniqueId)) {
            const cachedResult = audioCheckCacheRef.current.get(uniqueId)!;
            setAudioExistsMap((prev) => ({
              ...prev,
              [uniqueId]: cachedResult,
            }));
            continue;
          }

          // Check if audio exists
          try {
            const exists = await checkAudioExists(uniqueId);
            audioCheckCacheRef.current.set(uniqueId, exists);
            setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: exists }));

            console.log(`🎵 Audio check for ${uniqueId}: ${exists}`);
          } catch (error) {
            console.error(`🎵 Audio check error for ${uniqueId}:`, error);
            audioCheckCacheRef.current.set(uniqueId, false);
            setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: false }));
          }
        }
      }
    };

    checkAllAudio();
  }, [localFeedbackItems.length]);

  // Add effect to trigger data refresh when needed
  useEffect(() => {
    if (shouldRefreshAfterUpload && cardId) {
      console.log("♻️ Refreshing feedback data after upload completion...");

      // Refresh the current view by re-fetching data
      // You'll need to implement this based on your existing data fetching logic
      refreshFeedbackData();

      setShouldRefreshAfterUpload(false);
    }
  }, [shouldRefreshAfterUpload, cardId]);

  const refreshFeedbackData = async () => {
    if (!cardId) {
      console.warn("⚠️ No cardId available for refresh");
      return;
    }

    console.log("📡 Fetching fresh feedback data from API for card:", cardId);

    try {
      const accessToken = TokenManager.getAccessToken();
      if (!accessToken) {
        console.error("❌ No access token available for refresh");
        return;
      }

      const response = await fetch(
        API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        console.error("❌ Failed to fetch feedback data:", response.status);
        return;
      }

      const data = await response.json();
      console.log("✅ Fresh feedback data received:", data);

      // Transform API response to FeedbackItem format
      if (data.feedbacks && Array.isArray(data.feedbacks)) {
        const transformedItems: FeedbackItem[] = await Promise.all(
          data.feedbacks
            .filter(
              (fb: any) =>
                fb.feedback_message ||
                fb.feedback_screenshot ||
                fb.feedback_recording ||
                fb.feedback_voice,
            )
            .map(async (fb: any, index: number) => {
              const feedbackId = fb.feedback_id.toString();

              // Determine feedback type
              let itemType: "screenshot" | "recording" = "screenshot";
              if (fb.feedback_recording && !fb.feedback_screenshot) {
                itemType = "recording";
              } else if (fb.feedback_recording && fb.feedback_screenshot) {
                itemType = "recording";
              }

              const feedbackItem: FeedbackItem = {
                id: feedbackId,
                type: itemType,
                timestamp: new Date(fb.created_at).getTime(),
                order: index + 1,
                feedback: fb.feedback_message || "",
                hasAudio: !!fb.feedback_voice,
                audioUrl: fb.feedback_voice
                  ? `${BASE_URL}${fb.feedback_voice}`
                  : undefined,
                audioFilename: fb.feedback_voice
                  ? `audio_${fb.feedback_id}.mp3`
                  : undefined,
                user: {
                  name: "User",
                  avatar: AvatarIcon,
                },
                isSelected: false,
                apiFeedbackId: fb.feedback_id,
                uniqueFeedbackId: feedbackId,
                developerStatus:
                  fb.status === "reshare"
                    ? "reshare"
                    : fb.status === "acknowledged"
                      ? "acknowledged"
                      : fb.status === "unclear"
                        ? "unclear"
                        : null,
              };

              // ✅ ADD THIS: Immediately update audio cache after creating feedback item
              if (fb.feedback_voice && feedbackId) {
                console.log(
                  `✅ Audio detected for feedback ${feedbackId} from API`,
                );
                audioCheckCacheRef.current.set(feedbackId, true);
                setAudioExistsMap((prev) => ({ ...prev, [feedbackId]: true }));
              }

              // Handle screenshot
              if (itemType === "screenshot" && fb.feedback_screenshot) {
                const screenshotUrl = `${BASE_URL}${fb.feedback_screenshot}`;
                feedbackItem.annotatedImage = screenshotUrl;
                feedbackItem.originalImage = screenshotUrl;
              }

              // Handle recording
              if (itemType === "recording" && fb.feedback_recording) {
                const recordingUrl = `${BASE_URL}${fb.feedback_recording}`;
                feedbackItem.dataUrl = recordingUrl;
                feedbackItem.duration = 30;

                // Load recording blob
                try {
                  const blobResponse = await fetch(recordingUrl);
                  const blob = await blobResponse.blob();
                  feedbackItem.recordingBlob = blob;
                } catch (error) {
                  console.warn(
                    `⚠️ Failed to load recording blob for ${feedbackId}:`,
                    error,
                  );
                  feedbackItem.recordingBlob = new Blob([], {
                    type: "video/mp4",
                  });
                }
              }

              return feedbackItem;
            }),
        );

        // ✅ CRITICAL FIX: Sort by timestamp (oldest first, newest last)
        // This ensures new feedback appears at the bottom
        const sortedItems = transformedItems.sort((a, b) => {
          return a.timestamp - b.timestamp; // Ascending order: oldest → newest
        });

        // Update state with refreshed and sorted data
        setLocalFeedbackItems(sortedItems);
        console.log("✅ Feedback items updated and sorted:", {
          totalItems: sortedItems.length,
          withStatus: sortedItems.filter((item) => item.developerStatus).length,
          firstItemTimestamp: sortedItems[0]?.timestamp && !isNaN(sortedItems[0].timestamp) ? new Date(sortedItems[0].timestamp).toISOString() : "invalid",
          lastItemTimestamp: sortedItems[sortedItems.length - 1]?.timestamp && !isNaN(sortedItems[sortedItems.length - 1].timestamp) ? new Date(sortedItems[sortedItems.length - 1].timestamp).toISOString() : "invalid",
        });
      }
    } catch (error) {
      console.error("❌ Error refreshing feedback data:", error);
    }
  };

  // Listen for immediate card refresh events
  useEffect(() => {
    const handleImmediateRefresh = (event: Event) => {
      if (!(event instanceof CustomEvent)) return;

      const { cardId: refreshCardId, immediate } = event.detail;

      if (immediate && refreshCardId === cardId) {
        console.log("⚡ IMMEDIATE refresh triggered for card:", cardId);
        refreshFeedbackData(); // Call existing refresh function
      }
    };

    window.addEventListener(
      "refresh-card-data",
      handleImmediateRefresh as EventListener,
    );

    return () => {
      window.removeEventListener(
        "refresh-card-data",
        handleImmediateRefresh as EventListener,
      );
    };
  }, [cardId, refreshFeedbackData]);
  // Helper function to get expansion state for current feedback (defaults to true)
  const getCurrentDrawerExpanded = useCallback(() => {
    if (!selectedFeedback?.uniqueFeedbackId) return true;
    // Default to true (expanded) if no state exists yet
    return drawerExpandedState[selectedFeedback.uniqueFeedbackId] ?? true;
  }, [selectedFeedback?.uniqueFeedbackId, drawerExpandedState]);

  // Helper function to set expansion state for current feedback
  const setCurrentDrawerExpanded = useCallback(
    (expanded: boolean) => {
      if (!selectedFeedback?.uniqueFeedbackId) return;
      setDrawerExpandedState((prev) => ({
        ...prev,
        [selectedFeedback.uniqueFeedbackId!]: expanded,
      }));
      console.log(
        `🔖 Saved drawer state for feedback ${
          selectedFeedback.uniqueFeedbackId
        }: ${expanded ? "EXPANDED" : "COLLAPSED"}`,
      );
    },
    [selectedFeedback?.uniqueFeedbackId],
  );

  useEffect(() => {
    if (!selectedFeedback) return;

    const checkOverflow = () => {
      const element = textContentRefs.current.get(selectedFeedback.id);
      if (element) {
        const lineHeight = parseFloat(
          window.getComputedStyle(element).lineHeight,
        );
        const maxHeight = lineHeight * 1.5; // 1.5 lines
        const isOverflowing = element.scrollHeight > maxHeight + 1;

        setTextOverflowMap((prev) => {
          if (prev[selectedFeedback.id] !== isOverflowing) {
            return {
              ...prev,
              [selectedFeedback.id]: isOverflowing,
            };
          }
          return prev;
        });
      }
    };

    // Check immediately and after a small delay for DOM rendering
    checkOverflow();
    const timeoutId = setTimeout(checkOverflow, 100);

    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
      clearTimeout(timeoutId);
    };
  }, [
    selectedFeedback?.id,
    selectedFeedback?.feedback,
    selectedFeedback?.question,
  ]);

  useEffect(() => {
    let animationFrameId: number;

    const rafLoop = () => {
      const video = document.querySelector("video");
      if (video && !video.paused && !isNaN(video.currentTime)) {
        setVideoCurrentTime(video.currentTime);
      }
      animationFrameId = requestAnimationFrame(rafLoop);
    };

    animationFrameId = requestAnimationFrame(rafLoop);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []);

  // Effects (keep existing effects)
  useEffect(() => {
    setLocalFeedbackItems([...feedbackItems].sort((a, b) => a.timestamp - b.timestamp));

    // ✅ Initialize audio state for all items with audio
    if (feedbackItems.length > 0) {
      const audioMap: Record<string, boolean> = {};

      feedbackItems.forEach((item) => {
        if (item.uniqueFeedbackId && item.hasAudio) {
          audioMap[item.uniqueFeedbackId] = true;
          audioCheckCacheRef.current.set(item.uniqueFeedbackId, true);
        }
      });

      if (Object.keys(audioMap).length > 0) {
        setAudioExistsMap((prev) => ({ ...prev, ...audioMap }));
        console.log("🎵 Initialized audio state for items:", audioMap);
      }
    }
  }, [feedbackItems]);

  // Audio checking (keep existing implementation)
  const checkAudioForItem = useCallback(
    async (item: FeedbackItem) => {
      if (item.type !== "screenshot" || !item.uniqueFeedbackId) return;

      const uniqueId = item.uniqueFeedbackId;

      if (forceRecorderMode[uniqueId] || isUploadingAudio[uniqueId]) {
        console.log(
          `🎵 Skipping audio check - in force recorder mode or uploading: ${uniqueId}`,
        );
        return;
      }

      if (audioCheckCacheRef.current.has(uniqueId)) {
        const cachedResult = audioCheckCacheRef.current.get(uniqueId)!;
        setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: cachedResult }));
        console.log(
          `🎵 Using cached audio result for ${uniqueId}: ${cachedResult}`,
        );
        return;
      }

      setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: "loading" }));

      try {
        const exists = await checkAudioExists(uniqueId);

        audioCheckCacheRef.current.set(uniqueId, exists);
        setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: exists }));

        console.log(`🎵 Audio check completed for ${uniqueId}: ${exists}`);

        if (!exists && !forceRecorderMode[uniqueId]) {
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: false }));
        }
      } catch (error) {
        console.error(`🎵 Audio check error for ${uniqueId}:`, error);
        audioCheckCacheRef.current.set(uniqueId, false);
        setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: false }));
      }
    },
    [forceRecorderMode, isUploadingAudio],
  );

  // Initialize card and workspace IDs from props
  useEffect(() => {
    if (propCardId) setCardId(propCardId);
    if (propWorkspaceId) setWorkspaceId(propWorkspaceId);
  }, [propCardId, propWorkspaceId]);

  // Initialize submitted mode from props
  useEffect(() => {
    if (propIsSubmittedMode !== undefined) {
      setIsSubmittedMode(propIsSubmittedMode);
      setFeedbackSubmitted(propIsSubmittedMode);
    }
  }, [propIsSubmittedMode]);

  // ✅ useEffect #1 - Initialize statuses from pre-loaded feedback items (INSTANT)
  useEffect(() => {
    if (feedbackItems.length > 0 && isSubmittedMode && showDeveloperStatus) {
      const hasPreloadedStatuses = feedbackItems.some(
        (item) =>
          item.developerStatus !== undefined && item.developerStatus !== null,
      );

      if (hasPreloadedStatuses) {
        console.log(
          "✅ Using pre-loaded statuses from feedback items (INSTANT)",
        );

        const preloadedMap: Record<
          string,
          "acknowledged" | "unclear" | "reshare"
        > = {};
        const unclearDetailsMap: Record<string, any> = {};
        const profileImagesMap: Record<string, string | null> = {};
        const developerNamesMap: Record<string, string> = {}; // 🆕 ADD THIS LINE

        feedbackItems.forEach((item) => {
          if (item.uniqueFeedbackId && item.developerStatus) {
            preloadedMap[item.uniqueFeedbackId] = item.developerStatus;

            if (item.developerStatus === "unclear" && item.uniqueFeedbackId) {
              unclearDetailsMap[item.uniqueFeedbackId] = {
                status: "unclear",
                unclear_reason: ["Explanation"],
                comment: "",
                email: "",
                action_time: "",
              };
            }
          }
        });

        setFeedbackStatuses(preloadedMap);
        setDeveloperProfileImages(profileImagesMap);
        setDeveloperNames(developerNamesMap); // 🆕 ADD THIS LINE

        if (Object.keys(unclearDetailsMap).length > 0) {
          if (!window.__unclearDetailsCache) {
            window.__unclearDetailsCache = {};
          }
          Object.keys(unclearDetailsMap).forEach((key) => {
            if (!window.__unclearDetailsCache![key]) {
              window.__unclearDetailsCache![key] = unclearDetailsMap[key];
            }
          });
          console.log(
            "💾 Pre-cached unclear details for instant drawer:",
            unclearDetailsMap,
          );
        }

        console.log(
          "✅ Pre-loaded statuses initialized INSTANTLY:",
          preloadedMap,
        );
        return;
      }
    }
  }, [feedbackItems, isSubmittedMode, showDeveloperStatus]);

  // ✅ useEffect #2 - Background fetch (ONLY if no pre-loaded data)
  useEffect(() => {
    const fetchAllFeedbackStatusesFromCard = async () => {
      if (!isSubmittedMode || !showDeveloperStatus) {
        return;
      }

      if (feedbackItems.length === 0) {
        return;
      }

      if (!cardId) {
        console.warn("⚠️ No cardId available for batch status fetch");
        return;
      }

      console.log(
        "🔄 Fetching statuses using card-level API for card:",
        cardId,
      );

      try {
        const accessToken = TokenManager.getAccessToken();
        if (!accessToken) {
          return;
        }

        const response = await fetch(
          API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          console.error("❌ Failed to fetch card statuses:", response.status);
          return;
        }

        const data = await response.json();
        console.log("📊 Card status API response:", data);

        const statusMap: Record<
          string,
          "acknowledged" | "unclear" | "reshare"
        > = {};
        const unclearDetailsMap: Record<string, any> = {};
        const profileImagesMap: Record<string, string | null> = {};
        const developerNamesMap: Record<string, string> = {};

        // Parse the response structure
        if (data.feedbacks && Array.isArray(data.feedbacks)) {
          data.feedbacks.forEach((feedback: any) => {
            const feedbackId = feedback.feedback_id?.toString();
            if (!feedbackId) return;

            // ✅ CRITICAL PRIORITY FIX:
            // Check if there's a developer action FIRST (higher priority)
            if (
              feedback.developers &&
              Array.isArray(feedback.developers) &&
              feedback.developers.length > 0
            ) {
              const latestDeveloper = feedback.developers[0];

              if (latestDeveloper?.status) {
                const status = latestDeveloper.status.toLowerCase();

                if (
                  status === "acknowledged" ||
                  status === "unclear" ||
                  status === "reshare"
                ) {
                  // ✅ Developer action takes priority over card-level status
                  statusMap[feedbackId] = status as
                    | "acknowledged"
                    | "unclear"
                    | "reshare";

                  console.log(
                    `✅ Priority given to developer action for feedback ${feedbackId}: ${status}`,
                  );

                  // ✅ ONLY store profile image and name for acknowledged/unclear (NOT for reshare)
                  if (status !== "reshare") {
                    profileImagesMap[feedbackId] =
                      latestDeveloper.profile_image || null;

                    // Store developer name
                    if (
                      latestDeveloper.developer_name ||
                      latestDeveloper.developer_email
                    ) {
                      const displayName =
                        latestDeveloper.developer_name ||
                        latestDeveloper.developer_email.split("@")[0];
                      developerNamesMap[feedbackId] = displayName;

                      console.log(
                        `✅ Extracted developer name for ${feedbackId}: ${displayName}`,
                      );
                    }

                    // Cache unclear details
                    if (status === "unclear") {
                      unclearDetailsMap[feedbackId] = {
                        email: latestDeveloper.developer_email || "",
                        status: "unclear",
                        unclear_reason: latestDeveloper.unclear_reason || [],
                        comment: latestDeveloper.comment || "",
                        action_time: latestDeveloper.action_time || "",
                      };
                    }
                  }

                  // ✅ Skip card-level status check since developer action has priority
                  return;
                }
              }
            }

            // ✅ ONLY check card-level reshare status if NO developer action exists
            if (
              feedback.status &&
              feedback.status.toLowerCase() === "reshare"
            ) {
              statusMap[feedbackId] = "reshare";
              console.log(
                `✅ No developer action - using card-level reshare for feedback ${feedbackId}`,
              );
              return;
            }

            console.log(`ℹ️ No status found for feedback ${feedbackId}`);
          });
        }

        setFeedbackStatuses(statusMap);
        setDeveloperProfileImages(profileImagesMap);
        setDeveloperNames(developerNamesMap);
        window.__unclearDetailsCache = unclearDetailsMap;

        console.log("✅ Statuses loaded from card API:", statusMap);
        console.log("✅ Developer names loaded:", developerNamesMap);
      } catch (error) {
        console.error("❌ Error fetching card statuses:", error);
      }
    };

    if (
      feedbackItems.length > 0 &&
      isSubmittedMode &&
      showDeveloperStatus &&
      cardId
    ) {
      fetchAllFeedbackStatusesFromCard();
    }
  }, [feedbackItems, isSubmittedMode, showDeveloperStatus, cardId]);

  // Check audio for visible items
  useEffect(() => {
    if (
      selectedFeedback?.type === "screenshot" &&
      selectedFeedback.uniqueFeedbackId
    ) {
      checkAudioForItem(selectedFeedback);
    }

    const visibleItems = filteredFeedbackItems
      .filter((item) => item.type === "screenshot" && item.uniqueFeedbackId)
      .slice(0, 5);

    const timeoutId = setTimeout(() => {
      visibleItems.forEach((item, index) => {
        setTimeout(() => checkAudioForItem(item), index * 200);
      });
    }, 100);

    return () => clearTimeout(timeoutId);
    // FIXED: Use only the ID and callback - filteredFeedbackItems is already derived
  }, [selectedFeedback, checkAudioForItem]);

  // 🆕 NEW: Detect overflow in feedback list
  useEffect(() => {
    const checkOverflow = () => {
      if (feedbackListRef.current) {
        const isOverflowing =
          feedbackListRef.current.scrollHeight >
          feedbackListRef.current.clientHeight;
        setIsListOverflowing(isOverflowing);
      }
    };

    // Check on mount and when items change
    checkOverflow();

    // Check on window resize
    window.addEventListener("resize", checkOverflow);

    return () => window.removeEventListener("resize", checkOverflow);
  }, [filteredFeedbackItems.length]);

  // Sync blob updates from parent into localFeedbackItems
  useEffect(() => {
    setLocalFeedbackItems((prev) =>
      prev.map((localItem) => {
        const updated = feedbackItems.find((fi) => fi.id === localItem.id);
        if (updated && updated.recordingBlob !== localItem.recordingBlob) {
          return { ...localItem, recordingBlob: updated.recordingBlob };
        }
        return localItem;
      }),
    );
  }, [feedbackItems]);

  // Video URL management — also watch recordingBlob so URL updates when blob arrives
  useEffect(() => {
    if (selectedFeedback?.type !== "recording") {
      setVideoUrl(null);
      return;
    }

    const newVideoUrl = createVideoUrl(selectedFeedback);
    if (newVideoUrl && isValidVideoSource(newVideoUrl)) {
      setVideoUrl(newVideoUrl);
    } else {
      setVideoUrl(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeedback?.id, selectedFeedback?.recordingBlob]);

  useEffect(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (!video || !videoUrl) return;

    console.log("📹 Setting up new video:", {
      videoUrl,
      currentVolume: videoVolume,
      currentMuted: isVideoMuted,
    });

    // Set the source
    video.src = videoUrl;

    // Reset state for new video
    setIsVideoPlaying(false);
    setVideoCurrentTime(0);
    setVideoDuration(0);
    setVideoError(null);

    // Apply volume settings IMMEDIATELY
    video.volume = videoVolume;
    video.muted = isVideoMuted;

    console.log("📹 Video volume applied:", {
      videoVolume: video.volume,
      videoMuted: video.muted,
    });

    // Single handler for when duration is available
    const handleLoadedMetadata = () => {
      const duration = video.duration;
      if (!isNaN(duration) && duration > 0) {
        setVideoDuration(duration);

        // Reapply volume settings after metadata loads (some browsers reset it)
        video.volume = videoVolume;
        video.muted = isVideoMuted;

        console.log(
          "📹 Video ready - Duration:",
          duration,
          "Volume restored:",
          video.volume,
        );
      }
    };

    // Remove all old listeners
    video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    video.removeEventListener("play", handleVideoPlay);
    video.removeEventListener("pause", handleVideoPause);
    video.removeEventListener("ended", handleVideoEnded);
    video.removeEventListener("error", handleVideoErrorNative);

    // Add single listener
    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    };
  }, [videoUrl, videoVolume, isVideoMuted]);

  useEffect(() => {
    console.log("📹 Feedback changed:", {
      selectedFeedbackId,
      type: selectedFeedback?.type,
      hasVideoUrl: !!videoUrl,
      currentVolume: videoVolume,
      currentMuted: isVideoMuted,
    });
  }, [
    selectedFeedbackId,
    selectedFeedback?.type,
    videoUrl,
    videoVolume,
    isVideoMuted,
  ]);

  useEffect(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (!video) {
      console.log("🔊 No video element found for volume sync");
      return;
    }

    console.log("🔊 Syncing volume to video element:", {
      videoVolume,
      isVideoMuted,
      currentVideoVolume: video.volume,
      currentVideoMuted: video.muted,
      videoSrc: video.src,
    });

    video.volume = videoVolume;
    video.muted = isVideoMuted;
  }, [videoVolume, isVideoMuted, selectedFeedbackId]);

  // Reset video state on feedback change
  useEffect(() => {
    if (selectedFeedback?.type !== "recording") {
      setIsVideoPlaying(false);
      setVideoCurrentTime(0);
      setVideoDuration(0);
      setVideoError(null);
      setShowVolumeSlider(false);

      const video = document.querySelector("video");
      if (video) {
        video.pause();
        video.currentTime = 0;
        video.src = "";
      }
    }
  }, [selectedFeedback?.type]);

  // Force VoiceNoteRecorder re-render when necessary
  useEffect(() => {
    if (selectedFeedback?.type === "screenshot") {
      console.log("📸 ViewListFeedback - Selected feedback changed:", {
        selectedFeedbackId,
        uniqueFeedbackId: selectedFeedback?.uniqueFeedbackId,
        audioState: selectedFeedback?.uniqueFeedbackId
          ? audioExistsMap[selectedFeedback.uniqueFeedbackId]
          : "unknown",
      });

      setVoiceRecorderKey((prev) => prev + 1);
    }
  }, [selectedFeedbackId, selectedFeedback?.uniqueFeedbackId]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isSearchOpen &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setIsSearchOpen(false);
        setSearchQuery("");
      }
      if (
        isFilterOpen &&
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setIsFilterOpen(false);
      }
      if (
        showVolumeSlider &&
        volumeRef.current &&
        !volumeRef.current.contains(event.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
      // ✅ CRITICAL: This part handles the dropdown
      if (
        isDropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchOpen, isFilterOpen, showVolumeSlider, isDropdownOpen]);

  useEffect(() => {
    // Close dropdown when feedback selection changes
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [selectedFeedbackId]);

  // Focus management
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Helper function to show toast notifications
  const showToastNotification = useCallback(
    (message: string, type: "success" | "error" = "success") => {
      setToastMessage(message);
      setToastType(type);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    },
    [],
  );

  // Helper functions with better performance
  const hasExistingAudio = useCallback(
    (item: FeedbackItem): boolean => {
      if (item.type !== "screenshot" || !item.uniqueFeedbackId) return false;
      return audioExistsMap[item.uniqueFeedbackId] === true;
    },
    [audioExistsMap],
  );

  const isAudioLoading = useCallback(
    (item: FeedbackItem): boolean => {
      if (item.type !== "screenshot" || !item.uniqueFeedbackId) return false;
      return audioExistsMap[item.uniqueFeedbackId] === "loading";
    },
    [audioExistsMap],
  );

  // Upload voice recording to database with proper MP3 conversion - updated to match ScreenshotPreviewComponent
  const uploadVoiceRecording = useCallback(
    async (feedbackId: string, audioBlob: Blob, filename: string) => {
      if (!selectedFeedback?.uniqueFeedbackId) {
        console.error("No unique feedback ID for upload");
        return false;
      }

      const uniqueId = selectedFeedback.uniqueFeedbackId;

      setIsUploadingAudio((prev) => ({ ...prev, [uniqueId]: true }));
      setUploadError((prev) => {
        const updated = { ...prev };
        delete updated[uniqueId];
        return updated;
      });

      console.log("🔄 Starting voice upload process:", {
        feedbackId,
        uniqueId,
        blobSize: audioBlob.size,
        blobType: audioBlob.type,
        filename,
      });

      try {
        const result = await VoiceUploadManager.uploadVoiceRecording(
          uniqueId,
          audioBlob,
          filename,
        );

        if (result.success) {
          console.log("✅ Voice upload successful:", result);

          // Update cache and state to reflect successful upload
          audioCheckCacheRef.current.set(uniqueId, true);
          setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: true }));
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: false }));

          // Update feedback items
          const updatedItems = localFeedbackItems.map((item) => {
            if (item.id === feedbackId) {
              return {
                ...item,
                hasAudio: true,
                audioUrl: result.voice_url,
              };
            }
            return item;
          });
          setLocalFeedbackItems(updatedItems);

          // Call parent callback
          onUpdateAudio?.(feedbackId, true, result.voice_url);

          // Force re-render to show AudioPlayer
          setTimeout(() => {
            setVoiceRecorderKey((prev) => prev + 1);
          }, 100);

          return true;
        } else {
          throw new Error(result.error || "Upload failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Upload failed";
        console.error("❌ Voice upload failed:", errorMessage);

        setUploadError((prev) => ({ ...prev, [uniqueId]: errorMessage }));

        return false;
      } finally {
        setIsUploadingAudio((prev) => ({ ...prev, [uniqueId]: false }));
      }
    },
    [selectedFeedback, localFeedbackItems, onUpdateAudio],
  );

  // Video event handlers (keeping existing implementations)
  const handleVideoLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      if (!isNaN(duration) && duration > 0) {
        setVideoDuration(duration);
        setVideoCurrentTime(videoRef.current.currentTime || 0);
        setVideoError(null);
        console.log("📹 Video metadata loaded:", {
          duration,
          currentTime: videoRef.current.currentTime,
        });
      }
    }
  }, []);

  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setVideoCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleVideoEnded = useCallback(() => {
    setIsVideoPlaying(false);
    setVideoCurrentTime(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, []);

  const handleVideoPlay = useCallback(() => {
    setIsVideoPlaying(true);
    setVideoError(null);
  }, []);

  const handleVideoPause = useCallback(() => {
    setIsVideoPlaying(false);
  }, []);

  // React event handler for JSX onError prop
  const handleVideoError = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
      const video = e.currentTarget;
      const error = video.error;
      setVideoError(error ? `Video error: ${error.message}` : "Video error");
      setIsVideoPlaying(false);
    },
    [],
  );

  // Native DOM event handler for addEventListener
  const handleVideoErrorNative = useCallback((e: Event) => {
    const video = e.target as HTMLVideoElement;
    const error = video.error;
    setVideoError(error ? `Video error: ${error.message}` : "Video error");
    setIsVideoPlaying(false);
  }, []);

  const toggleVideoPlayPause = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;

    if (!video) {
      setVideoError("Video not available");
      return;
    }

    if (!videoUrl) {
      setVideoError("No video URL");
      return;
    }

    try {
      if (isVideoPlaying) {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise) {
          playPromise.catch((error) => {
            setVideoError("Playback failed: " + error.message);
          });
        }
      }
    } catch (error) {
      setVideoError(
        "Error: " + (error instanceof Error ? error.message : "unknown"),
      );
    }
  }, [isVideoPlaying, videoUrl]);

  const handleProgressBarClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const video = document.querySelector("video") as HTMLVideoElement;
      if (!video || videoDuration <= 0) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const percentage = (e.clientX - rect.left) / rect.width;
      const newTime = Math.max(
        0,
        Math.min(videoDuration, percentage * videoDuration),
      );

      video.currentTime = newTime;
      setVideoCurrentTime(newTime);
    },
    [videoDuration],
  );

  // Safe skip functions
  const skipBackward = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video) {
      video.currentTime = Math.max(0, video.currentTime - 5);
      setVideoCurrentTime(video.currentTime);
    }
  }, []);

  const skipForward = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;
    if (video && videoDuration) {
      video.currentTime = Math.min(videoDuration, video.currentTime + 5);
      setVideoCurrentTime(video.currentTime);
    }
  }, [videoDuration]);

  const toggleVideoMute = useCallback(() => {
    const video = document.querySelector("video") as HTMLVideoElement;

    console.log("🔊 Toggle mute clicked:", {
      videoExists: !!video,
      currentMuted: isVideoMuted,
      videoElement: video
        ? {
            src: video.src,
            muted: video.muted,
            volume: video.volume,
          }
        : null,
    });

    if (video) {
      const newMuted = !isVideoMuted;
      video.muted = newMuted;
      setIsVideoMuted(newMuted);

      console.log("🔊 After toggle:", {
        newMuted,
        videoMuted: video.muted,
        stateUpdated: true,
      });
    } else {
      console.error("🔊 ERROR: No video element found!");
    }
  }, [isVideoMuted]);

  const toggleVolumeSlider = useCallback(() => {
    console.log("🔊 Volume slider toggle clicked:", {
      currentState: showVolumeSlider,
      newState: !showVolumeSlider,
    });
    setShowVolumeSlider(!showVolumeSlider);
  }, [showVolumeSlider]);

  // Updated event handlers
  const handleContinueEditing = useCallback(() => {
    // ✅ DON'T call onBack() - that will close everything
    // Instead, check for completed uploads and navigate to workspace

    console.log("🔄 Continue editing - checking for new uploads...");

    if (cardId) {
      const completedUploads = UploadQueueManager.getCompletedForCard(cardId);
      console.log("📥 Completed uploads:", completedUploads.length);

      // Trigger immediate refresh in HomePage/FeedbackWorkspace
      window.dispatchEvent(
        new CustomEvent("refresh-card-data", {
          detail: {
            cardId: cardId,
            immediate: true,
            returnToWorkspace: true, // ✅ Signal to return to workspace
          },
        }),
      );
    }

    // Use dedicated continue-editing handler if provided, otherwise fall back to onBack
    if (onContinueEditing) {
      onContinueEditing();
    } else {
      onBack?.();
    }
  }, [onBack, onContinueEditing, cardId]);

  const handleBackToDashboard = useCallback(() => {
    console.log("⬅️ Back button clicked - returning to dashboard");

    // ✅ CRITICAL FIX: Always call onBack to properly close the view
    // This prevents full page reload and maintains cached data
    if (onBack) {
      onBack(); // Closes ViewListFeedback and returns to HomePage
    } else {
      // Fallback: Navigate to dashboard with preserved state
      navigate("/", {
        state: {
          activeTab: "all", // Return to the all tab
          preserveData: true, // Preserve cached data
        },
        replace: true,
      });
    }
  }, [navigate, onBack]);

  // NEW: Handle submit feedback button click
  const handleSubmitFeedbackClick = useCallback(() => {
    setShowSubmitConfirm(true);
  }, []);

  // NEW: Handle submit confirmation
  const handleSubmitConfirm = useCallback(() => {
    setShowSubmitConfirm(false);
    setFeedbackSubmitted(true);
    setIsSubmittedMode(true); // ✅ Set submitted mode

    // ✅ NEW: Open share popup immediately after submission
    setTimeout(() => {
      setIsSharePopupOpen(true);
    }, 500); // Small delay for smooth transition
  }, []);

  // NEW: Handle submit cancel
  const handleSubmitCancel = useCallback(() => {
    setShowSubmitConfirm(false);
  }, []);

  const handleShareWithDeveloper = useCallback(() => {
    // Double-check protection
    if (isSharing || (cardId && shareCallTracker.get(cardId))) {
      console.warn("🚫 Share button clicked but already sharing card:", cardId);
      return;
    }

    if (!cardId || cardId === null) {
      console.error("ViewListFeedback - No card ID available for sharing");
      showToastNotification("Unable to share: Card ID not found", "error");
      return;
    }

    console.log("📤 Opening share popup for card:", cardId);
    setIsSharePopupOpen(true);
  }, [cardId, isSharing]);

  // Enhanced share call tracker cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up any active share tracking when component unmounts
      if (cardId && shareCallTracker.has(cardId)) {
        shareCallTracker.delete(cardId);
        console.log("🧹 Cleaned up share tracker on unmount for card:", cardId);
      }
    };
  }, [cardId]);

  const handleShareEmails = useCallback(
    async (emails: string[]) => {
      if (!cardId) {
        console.error("ViewListFeedback - No card ID available for sharing");
        showToastNotification("Unable to share: Card ID not found", "error");
        return;
      }

      // Enhanced protection against multiple calls
      const shareKey = `share-${cardId}`;

      // Check if already sharing this specific card
      if (shareCallTracker.get(cardId) || isSharing) {
        console.warn("🚫 BLOCKED: Share already in progress for card:", cardId);
        return;
      }

      // IMMEDIATELY block any further calls
      shareCallTracker.set(cardId, true);
      setIsSharing(true);

      console.log("🔒 SHARE PROTECTION ACTIVATED for card:", cardId);

      // Clean up emails
      const uniqueEmails = Array.from(
        new Set(emails.map((email) => email.trim().toLowerCase())),
      ).filter((email) => email.length > 0);

      if (uniqueEmails.length === 0) {
        // Reset protection if no emails
        shareCallTracker.set(cardId, false);
        setIsSharing(false);
        showToastNotification("No valid email addresses provided", "error");
        return;
      }

      console.log("🎯 SINGLE CARD SHARE INITIATED:", {
        cardId,
        uniqueEmails,
        feedbackCount: totalFeedbacks,
        shareType: "Complete card (not individual feedback items)",
        expectedResult: "One email per developer with complete card access",
        timestamp: new Date().toISOString(),
        protectionActive: true,
      });

      // Close popup immediately to prevent re-clicking
      setIsSharePopupOpen(false);

      try {
        const result = await shareFeedbackWithDeveloper(
          cardId,
          uniqueEmails,
          workspaceId || undefined,
        );

        if (result.success && result.data) {
          console.log("✅ Card share completed successfully:", {
            cardId,
            resultData: result.data,
            timestamp: new Date().toISOString(),
          });

          // ✅ NEW: Set hasSharedOnce to true after first successful share
          setHasSharedOnce(true);

          // ✅ NEW: Store in localStorage for persistence
          if (cardId) {
            localStorage.setItem(`card_${cardId}_has_shared`, "true");
            console.log(`✅ Marked card ${cardId} as shared in localStorage`);
          }

          const expectedUrls = uniqueEmails.length;
          const actualUrls = result.data.developer_feedback_urls.length;

          // Enhanced validation
          if (actualUrls !== expectedUrls) {
            console.error("🚨 CRITICAL: URL COUNT MISMATCH DETECTED!", {
              expected: expectedUrls,
              actual: actualUrls,
              cardId,
              emails: uniqueEmails,
              urls: result.data.developer_feedback_urls,
              possibleCauses: [
                "API creating multiple emails per developer",
                "API sending emails per feedback item instead of per card",
                "Backend bug in card sharing logic",
              ],
            });
          }

          // Check for duplicate emails to same developer
          const emailCounts = new Map();
          result.data.developer_feedback_urls.forEach((item) => {
            const count = emailCounts.get(item.email) || 0;
            emailCounts.set(item.email, count + 1);
          });

          const duplicateEmails = Array.from(emailCounts.entries()).filter(
            ([email, count]) => count > 1,
          );
          if (duplicateEmails.length > 0) {
            console.error("🚨 DUPLICATE EMAILS DETECTED:", {
              duplicateEmails: duplicateEmails.map(([email, count]) => ({
                email,
                count,
              })),
              message:
                "Same developer receiving multiple emails - this should not happen for card sharing",
            });
          }

          // Success message
          const successMessage =
            actualUrls === expectedUrls
              ? `Complete feedback card shared with ${uniqueEmails.length} developer(s). Each will receive one email with access to all feedback items.`
              : `Card shared but received ${actualUrls} URLs for ${expectedUrls} developers. Please check if multiple emails were sent.`;

          showToastNotification(successMessage, "success");

          onShareWithDeveloper?.({
            success: true,
            cardId: cardId,
            feedbackName: feedbackName || "Untitled Feedback",
            emails: uniqueEmails,
            feedbackCount: totalFeedbacks || 0,
          });
        } else {
          throw new Error(result.error || "Share failed");
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error("❌ Card share failed:", {
          error: errorMessage,
          cardId,
          emails: uniqueEmails,
          timestamp: new Date().toISOString(),
        });
        showToastNotification(
          `Failed to share feedback card: ${errorMessage}`,
          "error",
        );

        // Re-open popup on error so user can retry
        setIsSharePopupOpen(true);
      } finally {
        // Always clean up protection with delay to prevent rapid re-clicks
        setTimeout(() => {
          shareCallTracker.set(cardId, false);
          setIsSharing(false);
          console.log("🔓 SHARE PROTECTION RELEASED for card:", cardId);
        }, 2000); // 2 second delay to prevent accidental double-clicks
      }
    },
    [
      cardId,
      workspaceId,
      totalFeedbacks,
      feedbackName,
      onShareWithDeveloper,
      isSharing,
    ],
  );

  // Helper function to scroll left sidebar to show selected item
  const scrollToFeedbackItem = useCallback((feedbackId: string) => {
    const feedbackElement = feedbackItemRefs.current.get(feedbackId);
    const listContainer = feedbackListRef.current;

    if (feedbackElement && listContainer) {
      const containerRect = listContainer.getBoundingClientRect();
      const elementRect = feedbackElement.getBoundingClientRect();

      // Calculate if element is not fully visible
      const isAboveView = elementRect.top < containerRect.top;
      const isBelowView = elementRect.bottom > containerRect.bottom;

      if (isAboveView || isBelowView) {
        // Scroll with smooth behavior and center the item
        const scrollOffset =
          elementRect.top -
          containerRect.top -
          (containerRect.height - elementRect.height) / 2;
        listContainer.scrollBy({
          top: scrollOffset,
          behavior: "smooth",
        });
      }
    }
  }, []);

  const handleFeedbackSelect = useCallback(
    (feedbackId: string) => {
      if (feedbackId === selectedFeedbackId || isAnimating) return;

      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );
      const nextIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === feedbackId,
      );

      if (currentIndex !== -1 && nextIndex !== -1) {
        const direction = nextIndex > currentIndex ? "down" : "up";

        // Store the current feedback as previous
        setPreviousFeedback(selectedFeedback || null);
        setScrollDirection(direction);
        setIsAnimating(true);

        // Set new feedback immediately
        setSelectedFeedbackId(feedbackId);

        // ✅ NEW: Scroll left sidebar to show selected item
        scrollToFeedbackItem(feedbackId);

        // Clear animation state after animation completes
        setTimeout(() => {
          setIsAnimating(false);
          setScrollDirection(null);
          setPreviousFeedback(null);
        }, 300);
      } else {
        setSelectedFeedbackId(feedbackId);
        // ✅ NEW: Scroll left sidebar to show selected item
        scrollToFeedbackItem(feedbackId);
      }
    },
    [
      selectedFeedbackId,
      selectedFeedback,
      filteredFeedbackItems,
      isAnimating,
      scrollToFeedbackItem,
    ],
  );
  // Keyboard navigation for scrolling through feedback items
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (showEditPopup || isSearchOpen) return;

      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );

      if (e.key === "ArrowUp" && currentIndex > 0) {
        e.preventDefault();
        handleFeedbackSelect(filteredFeedbackItems[currentIndex - 1].id);
      } else if (
        e.key === "ArrowDown" &&
        currentIndex < filteredFeedbackItems.length - 1
      ) {
        e.preventDefault();
        handleFeedbackSelect(filteredFeedbackItems[currentIndex + 1].id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedFeedbackId,
    filteredFeedbackItems,
    showEditPopup,
    isSearchOpen,
    handleFeedbackSelect,
  ]);

  // Wheel/scroll navigation with debouncing
  useEffect(() => {
    let isScrolling = false;
    let scrollTimeout: NodeJS.Timeout;

    const handleWheel = (e: WheelEvent) => {
      if (
        !contentContainerRef.current ||
        showEditPopup ||
        isAnimating ||
        isScrolling
      )
        return;

      const container = contentContainerRef.current;
      const target = e.target as HTMLElement;

      // Check if scrolling within the content container
      if (!container.contains(target)) return;

      // Check if we're scrolling within a scrollable element (like the video player)
      const isScrollable = (element: HTMLElement): boolean => {
        const overflowY = window.getComputedStyle(element).overflowY;
        return overflowY === "scroll" || overflowY === "auto";
      };

      let currentElement = target;
      while (currentElement && currentElement !== container) {
        if (
          isScrollable(currentElement) &&
          currentElement.scrollHeight > currentElement.clientHeight
        ) {
          // Allow natural scrolling within scrollable elements
          return;
        }
        currentElement = currentElement.parentElement as HTMLElement;
      }

      const currentIndex = filteredFeedbackItems.findIndex(
        (item) => item.id === selectedFeedbackId,
      );

      // Determine scroll direction (only trigger on significant scroll)
      const scrollThreshold = 10; // Minimum delta to trigger scroll

      if (Math.abs(e.deltaY) < scrollThreshold) return;

      if (e.deltaY > 0 && currentIndex < filteredFeedbackItems.length - 1) {
        e.preventDefault();
        isScrolling = true;
        handleFeedbackSelect(filteredFeedbackItems[currentIndex + 1].id);

        // Clear any existing timeout
        clearTimeout(scrollTimeout);

        // Reset scrolling flag after animation completes
        scrollTimeout = setTimeout(() => {
          isScrolling = false;
        }, 500); // Slightly longer than animation duration (300ms)
      } else if (e.deltaY < 0 && currentIndex > 0) {
        e.preventDefault();
        isScrolling = true;
        handleFeedbackSelect(filteredFeedbackItems[currentIndex - 1].id);

        // Clear any existing timeout
        clearTimeout(scrollTimeout);

        // Reset scrolling flag after animation completes
        scrollTimeout = setTimeout(() => {
          isScrolling = false;
        }, 500); // Slightly longer than animation duration (300ms)
      }
    };

    const container = contentContainerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        container.removeEventListener("wheel", handleWheel);
        clearTimeout(scrollTimeout);
      };
    }
  }, [
    selectedFeedbackId,
    filteredFeedbackItems,
    showEditPopup,
    isAnimating,
    handleFeedbackSelect,
  ]);
  useEffect(() => {
    // When filter changes, select the first item in the filtered list
    if (filteredFeedbackItems.length > 0) {
      const firstItemId = filteredFeedbackItems[0].id;

      // Only update if it's different from current selection
      if (selectedFeedbackId !== firstItemId) {
        setSelectedFeedbackId(firstItemId);

        // Also scroll to show the selected item in the sidebar
        scrollToFeedbackItem(firstItemId);
      }
    } else {
      // If no items in filtered list, clear selection
      setSelectedFeedbackId(null);
    }
  }, [selectedFilter, filteredFeedbackItems.length]);

  useEffect(() => {
    const highlightFeedbackId = localStorage.getItem("highlight_feedback_id");
    const developerAction = localStorage.getItem("developer_action");

    if (highlightFeedbackId && localFeedbackItems.length > 0) {
      console.log("🎯 Highlighting feedback from notification:", {
        feedbackId: highlightFeedbackId,
        action: developerAction,

        totalItems: localFeedbackItems.length,
      });

      // Wait for DOM to render
      setTimeout(() => {
        // Find the feedback item - check multiple ID formats
        const targetFeedback = localFeedbackItems.find(
          (item) =>
            item.uniqueFeedbackId === highlightFeedbackId ||
            item.id === highlightFeedbackId ||
            String(item.id) === highlightFeedbackId ||
            String(item.uniqueFeedbackId) === highlightFeedbackId ||
            String(item.apiFeedbackId) === highlightFeedbackId ||
            item.id.includes(highlightFeedbackId),
        );

        if (targetFeedback) {
          console.log("✅ Found target feedback:", {
            id: targetFeedback.id,
            uniqueFeedbackId: targetFeedback.uniqueFeedbackId,
            apiFeedbackId: targetFeedback.apiFeedbackId,
            type: targetFeedback.type,
          });

          // ✅ CRITICAL: Select the feedback first
          setSelectedFeedbackId(targetFeedback.id);

          // ✅ Then scroll to it in the left sidebar
          setTimeout(() => {
            const feedbackElement = feedbackItemRefs.current.get(
              targetFeedback.id,
            );

            if (feedbackElement) {
              console.log("📜 Scrolling to feedback in sidebar");

              feedbackElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });

              // Add highlight animation
              feedbackElement.classList.add("feedback-highlight-pulse");
              setTimeout(() => {
                feedbackElement.classList.remove("feedback-highlight-pulse");
              }, 3000);
            } else {
              console.warn("⚠️ Feedback element not found in refs");
            }
          }, 300); // Give time for selection to render
        } else {
          console.warn("⚠️ Feedback not found in list:", {
            searchingFor: highlightFeedbackId,
            availableIds: localFeedbackItems.map((item) => ({
              id: item.id,
              uniqueFeedbackId: item.uniqueFeedbackId,
              apiFeedbackId: item.apiFeedbackId,
            })),
          });
        }

        // Clear flags after processing
        localStorage.removeItem("highlight_feedback_id");
        localStorage.removeItem("developer_action");
        localStorage.removeItem("feedback_reshare_action");
      }, 500); // Increased delay to ensure DOM is ready
    }
  }, [localFeedbackItems, setSelectedFeedbackId]);

  const handleSearchClick = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const handleFilterClick = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
  }, [isFilterOpen]);

  const handleFilterSelect = useCallback(
    (
      filter:
        | "all"
        | "screenshot"
        | "recording"
        | "acknowledged"
        | "unclear"
        | "reshare" // ✅ ADDED
        | "not_read",
    ) => {
      setSelectedFilter(filter);
      setIsFilterOpen(false);
    },
    [],
  );
  const handleEditFeedback = useCallback(
    (feedbackId: string) => {
      const feedback = localFeedbackItems.find(
        (item) => item.id === feedbackId,
      );
      if (feedback) {
        setEditedFeedbackText(feedback.feedback || feedback.question || "");
        setIsDropdownOpen(false); // ✅ ADD THIS LINE - Close dropdown before opening popup
        setShowEditPopup(true);
      }
    },
    [localFeedbackItems],
  );

  const handleShowMoreClick = useCallback(() => {
    if (selectedFeedback) {
      setEditedFeedbackText(
        selectedFeedback.feedback || selectedFeedback.question || "",
      );
      setShowEditPopup(true);
      setIsDropdownOpen(false);
    }
  }, [selectedFeedback]);

  const handleSaveEdit = useCallback(async () => {
    if (selectedFeedback && editedFeedbackText.trim()) {
      setIsSavingEdit(true); // ✅ NEW: Add loading state
      try {
        const accessToken = TokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token available for feedback update");
          showToastNotification(
            "Unable to update feedback: No access token",
            "error",
          ); // ✅ NEW: Toast notification
          return;
        }

        // Get the correct feedback ID - use uniqueFeedbackId for screenshots
        let feedbackIdForUpdate;
        if (
          selectedFeedback.type === "screenshot" &&
          selectedFeedback.uniqueFeedbackId
        ) {
          feedbackIdForUpdate = selectedFeedback.uniqueFeedbackId;
        } else {
          feedbackIdForUpdate = selectedFeedback.id;
        }

        // Ensure we have a valid numeric ID
        const numericFeedbackId = parseInt(
          String(feedbackIdForUpdate).replace(/[^0-9]/g, ""),
        );

        if (isNaN(numericFeedbackId) || numericFeedbackId <= 0) {
          console.error("Invalid feedback ID for update:", {
            originalId: selectedFeedback.id,
            uniqueFeedbackId: selectedFeedback.uniqueFeedbackId,
            extractedId: numericFeedbackId,
          });
          showToastNotification(
            "Unable to update feedback: Invalid feedback ID",
            "error",
          ); // ✅ NEW: Toast notification
          return;
        }

        const message = editedFeedbackText.trim();

        console.log("Updating message:", {
          messageLength: message.length,
          feedbackId: numericFeedbackId,
          feedbackType: selectedFeedback.type,
          messagePreview:
            message.substring(0, 100) + (message.length > 100 ? "..." : ""),
        });

        const requestBody = {
          message: message,
        };

        // Make API call to update feedback
        const response = await fetch(
          API_URLS.PUT_UPDATE_FEEDBACK_BY_FEEDBACK_ID(numericFeedbackId),
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) {
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = `HTTP ${response.status}: ${errorText}`;
            }
          } catch (e) {
            // Use default message
          }
          throw new Error(errorMessage);
        }

        const result = await response.json();
        console.log("Message updated successfully:", result);

        // Update local state only after successful API call
        const updatedItems = localFeedbackItems.map((item) => {
          if (item.id === selectedFeedback.id) {
            return { ...item, feedback: message };
          }
          return item;
        });

        setLocalFeedbackItems(updatedItems);
        onUpdateFeedback?.(selectedFeedback.id, message);

        // ✅ NEW: Show success message and close popup
        showToastNotification("Feedback updated successfully", "success");
        setShowEditPopup(false);
        setEditedFeedbackText("");
      } catch (error) {
        console.error("Error updating message:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        showToastNotification(
          `Failed to update feedback: ${errorMessage}`,
          "error",
        ); // ✅ NEW: Error toast
      } finally {
        setIsSavingEdit(false); // ✅ NEW: Reset loading state
      }
    }
  }, [
    selectedFeedback,
    editedFeedbackText,
    localFeedbackItems,
    onUpdateFeedback,
    showToastNotification, // ✅ NEW: Add dependency
  ]);

  const handleCancelEdit = useCallback(() => {
    setShowEditPopup(false);
    setEditedFeedbackText("");
  }, []);

  // Enhanced recording completion handler with proper MP3 upload functionality
  const handleRecordingComplete = useCallback(
    async (
      hasRecording: boolean,
      audioUrl?: string,
      downloadUrl?: string,
      filename?: string,
    ) => {
      if (!selectedFeedback?.uniqueFeedbackId) {
        console.warn("No selected feedback or unique feedback ID");
        return;
      }

      console.log("Recording complete:", {
        selectedFeedbackId: selectedFeedback.id,
        uniqueFeedbackId: selectedFeedback.uniqueFeedbackId,
        hasRecording,
        audioUrl: audioUrl ? "provided" : "none",
        filename,
      });

      const uniqueId = selectedFeedback.uniqueFeedbackId;

      if (hasRecording && audioUrl && filename) {
        // Fetch the audio blob from the URL
        const response = await fetch(audioUrl);
        const audioBlob = await response.blob();

        // Update local state immediately for better UX
        setAudioExistsMap((prev) => ({
          ...prev,
          [uniqueId]: "loading", // Set to loading while uploading
        }));

        const updatedItems = localFeedbackItems.map((item) => {
          if (item.id === selectedFeedback.id) {
            return {
              ...item,
              hasAudio: true,
              audioUrl: audioUrl,
            };
          }
          return item;
        });

        setLocalFeedbackItems(updatedItems);

        // Start upload process with MP3 conversion
        const uploadSuccess = await uploadVoiceRecording(
          selectedFeedback.id,
          audioBlob,
          filename,
        );

        if (!uploadSuccess) {
          console.error("Upload failed, keeping in recorder mode");
          // Reset states if upload failed
          setAudioExistsMap((prev) => ({
            ...prev,
            [uniqueId]: false,
          }));
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: true }));
        }
      } else {
        // No recording or recording failed
        audioCheckCacheRef.current.set(uniqueId, false);

        setAudioExistsMap((prev) => ({
          ...prev,
          [uniqueId]: false,
        }));

        setForceRecorderMode((prev) => ({
          ...prev,
          [uniqueId]: true,
        }));

        const updatedItems = localFeedbackItems.map((item) => {
          if (item.id === selectedFeedback.id) {
            return {
              ...item,
              hasAudio: false,
              audioUrl: undefined,
            };
          }
          return item;
        });

        setLocalFeedbackItems(updatedItems);
        onUpdateAudio?.(selectedFeedback.id, false);
      }

      console.log("Recording completion handled successfully");
    },
    [selectedFeedback, localFeedbackItems, onUpdateAudio, uploadVoiceRecording],
  );

  // Enhanced audio deletion handler
  const handleAudioDelete = useCallback(
    (feedbackId: string, uniqueFeedbackId: string) => {
      console.log("Audio delete requested:", {
        feedbackId,
        uniqueFeedbackId,
      });

      // Immediately update cache and force recorder mode
      if (uniqueFeedbackId) {
        audioCheckCacheRef.current.set(uniqueFeedbackId, false);

        setAudioExistsMap((prev) => ({
          ...prev,
          [uniqueFeedbackId]: false,
        }));

        setForceRecorderMode((prev) => ({
          ...prev,
          [uniqueFeedbackId]: true,
        }));

        // Clear any upload errors
        setUploadError((prev) => {
          const updated = { ...prev };
          delete updated[uniqueFeedbackId];
          return updated;
        });
      }

      // Update feedback items immediately
      const updatedItems = localFeedbackItems.map((item) => {
        if (item.id === feedbackId) {
          return {
            ...item,
            hasAudio: false,
            audioUrl: undefined,
          };
        }
        return item;
      });

      setLocalFeedbackItems(updatedItems);

      // Call parent callback
      onUpdateAudio?.(feedbackId, false);

      // Force immediate re-render to show recorder
      setVoiceRecorderKey((prev) => prev + 1);

      console.log("Audio deletion completed, showing recorder");
    },
    [localFeedbackItems, onUpdateAudio],
  );

  // Enhanced audio player error handler
  const handleAudioPlayerError = useCallback(
    (error: string) => {
      console.error("Audio player error:", error);

      if (selectedFeedback?.uniqueFeedbackId) {
        const uniqueId = selectedFeedback.uniqueFeedbackId;

        // On error, mark as no audio and show recorder
        audioCheckCacheRef.current.set(uniqueId, false);

        setAudioExistsMap((prev) => ({
          ...prev,
          [uniqueId]: false,
        }));

        // Force recorder mode on error
        setForceRecorderMode((prev) => ({
          ...prev,
          [uniqueId]: true,
        }));

        // Clear any upload states
        setUploadError((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });

        // Force re-render to show recorder
        setVoiceRecorderKey((prev) => prev + 1);
      }
    },
    [selectedFeedback?.uniqueFeedbackId],
  );
  const fetchUnclearFeedbackDetails = useCallback(
    async (feedbackId: string) => {
      // ✅ NEW: Check if we've already fetched this feedback's details
      if (fetchedUnclearDetailsRef.current.has(feedbackId)) {
        console.log(
          "⚡ Already fetched details for:",
          feedbackId,
          "- skipping",
        );
        return;
      }

      if (!cardId) {
        console.error("❌ No cardId available - cannot fetch unclear details");
        setShowUnclearDrawer(false);
        setUnclearFeedbackData(null);
        return;
      }

      // Check cache
      const cachedData = window.__unclearDetailsCache?.[feedbackId];
      if (
        cachedData &&
        cachedData.unclear_reason?.length > 0 &&
        !(
          cachedData.unclear_reason.length === 1 &&
          cachedData.unclear_reason[0] === "Explanation"
        )
      ) {
        console.log(
          "⚡ Using complete cached data, skipping API call for:",
          feedbackId,
        );
        // ✅ Mark as fetched even if from cache
        fetchedUnclearDetailsRef.current.add(feedbackId);
        return;
      }

      try {
        const accessToken = TokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token for fetching unclear details");
          return;
        }

        console.log(
          "🔍 Fetching unclear details using card API for:",
          feedbackId,
        );

        // ✅ Mark as fetching BEFORE the API call to prevent duplicates
        fetchedUnclearDetailsRef.current.add(feedbackId);

        const response = await fetch(
          API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          console.error("❌ Failed to fetch card statuses:", response.status);
          // ✅ Remove from fetched set on error so it can be retried
          fetchedUnclearDetailsRef.current.delete(feedbackId);
          setShowUnclearDrawer(false);
          setUnclearFeedbackData(null);
          return;
        }

        const data = await response.json();

        if (data.feedbacks && Array.isArray(data.feedbacks)) {
          const targetFeedback = data.feedbacks.find(
            (f: any) => f.feedback_id?.toString() === feedbackId,
          );

          if (targetFeedback?.developers?.[0]?.status === "unclear") {
            const unclearStatus = targetFeedback.developers[0];

            const detailsData = {
              email: unclearStatus.developer_email || "",
              status: "unclear",
              unclear_reason: unclearStatus.unclear_reason || [],
              comment: unclearStatus.comment || "",
              action_time: unclearStatus.action_time || "",
            };

            console.log("✅ Found unclear status from card API:", detailsData);
            setUnclearFeedbackData(detailsData);
            setShowUnclearDrawer(true);

            if (!window.__unclearDetailsCache) {
              window.__unclearDetailsCache = {};
            }
            window.__unclearDetailsCache[feedbackId] = detailsData;
          } else {
            console.log("ℹ️ No unclear status found for feedback:", feedbackId);
            setShowUnclearDrawer(false);
            setUnclearFeedbackData(null);
          }
        } else {
          console.warn("⚠️ Invalid response format from card API");
          setShowUnclearDrawer(false);
          setUnclearFeedbackData(null);
        }
      } catch (error) {
        console.error("❌ Error fetching unclear feedback details:", error);
        // ✅ Remove from fetched set on error
        fetchedUnclearDetailsRef.current.delete(feedbackId);
        setShowUnclearDrawer(false);
        setUnclearFeedbackData(null);
      }
    },
    [cardId],
  );

  useEffect(() => {
    const currentFeedbackId = selectedFeedback?.uniqueFeedbackId;

    if (!currentFeedbackId) {
      setShowUnclearDrawer(false);
      setUnclearFeedbackData(null);
      return;
    }

    const status = feedbackStatuses[currentFeedbackId];

    console.log("🔍 Feedback selection changed:", {
      feedbackId: currentFeedbackId,
      status,
      hasCachedDetails: !!window.__unclearDetailsCache?.[currentFeedbackId],
      cachedData: window.__unclearDetailsCache?.[currentFeedbackId],
    });

    if (isSubmittedMode && showDeveloperStatus && status === "unclear") {
      console.log("📥 Feedback has unclear status");

      const cachedDetails = window.__unclearDetailsCache?.[currentFeedbackId];

      if (cachedDetails) {
        console.log(
          "⚡ Using cached unclear details (from full card API):",
          cachedDetails,
        );

        setUnclearFeedbackData(cachedDetails);
        setShowUnclearDrawer(true);

        // ✅ FIXED: Only set expanded state if it's the first time (undefined)
        // Don't change state if user has already toggled it
        if (drawerExpandedState[currentFeedbackId] === undefined) {
          console.log(
            "📂 First time showing this feedback - auto-expanding drawer",
          );
          setDrawerExpandedState((prev) => ({
            ...prev,
            [currentFeedbackId]: true,
          }));
        }

        // ✅ FIXED: Only fetch if NOT already fetched AND data is incomplete
        const alreadyFetched =
          fetchedUnclearDetailsRef.current.has(currentFeedbackId);
        const isIncomplete =
          cachedDetails.unclear_reason?.length === 0 ||
          (cachedDetails.unclear_reason?.length === 1 &&
            cachedDetails.unclear_reason?.[0] === "Explanation");

        if (!alreadyFetched && isIncomplete) {
          console.log(
            "🔄 Cached data is minimal and not yet fetched, fetching full details...",
          );
          const timeoutId = setTimeout(() => {
            fetchUnclearFeedbackDetails(currentFeedbackId);
          }, 100);
          return () => clearTimeout(timeoutId);
        }
      } else {
        console.log("⚠️ Cache miss - showing loading and fetching from API...");
        setUnclearFeedbackData({
          email: "",
          status: "unclear",
          unclear_reason: ["Loading details..."],
          comment: "",
          action_time: "",
        });
        setShowUnclearDrawer(true);

        if (drawerExpandedState[currentFeedbackId] === undefined) {
          setDrawerExpandedState((prev) => ({
            ...prev,
            [currentFeedbackId]: true,
          }));
        }

        const timeoutId = setTimeout(() => {
          fetchUnclearFeedbackDetails(currentFeedbackId);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    } else {
      console.log("🚫 Not unclear status - hiding drawer immediately");
      setShowUnclearDrawer(false);
      setUnclearFeedbackData(null);
    }
  }, [
    selectedFeedback?.uniqueFeedbackId,
    feedbackStatuses,
    isSubmittedMode,
    showDeveloperStatus,
    fetchUnclearFeedbackDetails,
    // ✅ REMOVED drawerExpandedState from dependencies - it was causing re-fetches
  ]);
  const handleNoAudioClick = useCallback(() => {
    setAudioErrorMessage("No audio available for this feedback");
    setShowAudioError(true);
    setTimeout(() => setShowAudioError(false), 3000);
  }, []);

  // 🆕 NEW: Handle "Noted & update" button click
  const handleNotedAndUpdate = useCallback(async () => {
    if (!selectedFeedback) {
      console.error("❌ Cannot update: no feedback selected");
      return;
    }

    console.log("✏️ Opening editor for unclear feedback:", {
      feedbackId: selectedFeedback.uniqueFeedbackId,
      type: selectedFeedback.type,
      hasFeedback: !!selectedFeedback.feedback,
    });

    // ✅ Handle Screenshot
    if (selectedFeedback.type === "screenshot") {
      // Prepare screenshot data for editing
      const screenshotDataUrl =
        selectedFeedback.annotatedImage || selectedFeedback.originalImage || "";

      if (!screenshotDataUrl) {
        console.error("❌ No screenshot image available");
        showToastNotification(
          "Cannot edit: Screenshot image not found",
          "error",
        );
        return;
      }

      // Fetch audio data if available
      let audioUrl = selectedFeedback.audioUrl;
      let audioBlob = selectedFeedback.audioBlob;

      // If we have a uniqueFeedbackId but no local audio data, try to fetch from API
      if (selectedFeedback.uniqueFeedbackId && !audioUrl && !audioBlob) {
        const audioExists = audioExistsMap[selectedFeedback.uniqueFeedbackId];
        if (audioExists === true) {
          audioUrl = `${API_URLS.GET_FILE_VOICE(
            selectedFeedback.uniqueFeedbackId,
          )}`;
        }
      }

      setEditingScreenshotData({
        dataUrl: screenshotDataUrl,
        feedbackId: selectedFeedback.uniqueFeedbackId || selectedFeedback.id,
        feedback: selectedFeedback.feedback || "",
        audioUrl,
        audioBlob,
        audioFilename: selectedFeedback.audioFilename,
      });

      // Close unclear drawer and open screenshot editor
      setShowUnclearDrawer(false);
      setShowEditScreenshotPreview(true);
    }
    // ✅ Handle Screen Recording
    else if (selectedFeedback.type === "recording") {
      const recordingUrl = createVideoUrl(selectedFeedback);

      if (!recordingUrl || !isValidVideoSource(recordingUrl)) {
        console.error("❌ No valid recording source available");
        showToastNotification(
          "Cannot edit: Recording source not found",
          "error",
        );
        return;
      }

      console.log("✏️ Opening recording editor:", {
        recordingUrl: recordingUrl.substring(0, 50),
        hasBlob: !!selectedFeedback.recordingBlob,
        duration: selectedFeedback.duration,
      });

      setEditingRecordingData({
        dataUrl: recordingUrl,
        feedbackId: selectedFeedback.uniqueFeedbackId || selectedFeedback.id,
        feedback: selectedFeedback.feedback || selectedFeedback.question || "",
        recordingBlob: selectedFeedback.recordingBlob,
        duration: selectedFeedback.duration || 0,
        thumbnailUrl: selectedFeedback.thumbnailUrl,
      });

      // Close unclear drawer and open recording editor
      setShowUnclearDrawer(false);
      setShowEditRecordingPreview(true);
    } else {
      console.error("❌ Unknown feedback type:", selectedFeedback.type);
      showToastNotification("Cannot edit: Unknown feedback type", "error");
    }
  }, [selectedFeedback, audioExistsMap, showToastNotification]);

  //  Handle screenshot edit submission
  const handleEditScreenshotSubmit = useCallback(
    async (submission: ScreenshotSubmission) => {
      console.log("✏️ Screenshot edit submitted:", {
        id: submission.id,
        uniqueFeedbackId: submission.uniqueFeedbackId,
        hasAudio: submission.hasAudio,
        audioUrl: submission.audioUrl?.substring(0, 50),
      });

      if (!selectedFeedback) return;

      const uniqueId = selectedFeedback.uniqueFeedbackId;

      // Clear audio cache and force states BEFORE updating items
      if (uniqueId) {
        console.log("🔄 Clearing audio cache for:", uniqueId);

        audioCheckCacheRef.current.delete(uniqueId);

        if (submission.hasAudio) {
          console.log("✅ Audio exists in submission - setting to true");
          audioCheckCacheRef.current.set(uniqueId, true);
          setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: true }));
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: false }));
        } else {
          console.log("❌ No audio in submission - setting to false");
          audioCheckCacheRef.current.set(uniqueId, false);
          setAudioExistsMap((prev) => ({ ...prev, [uniqueId]: false }));
          setForceRecorderMode((prev) => ({ ...prev, [uniqueId]: true }));
        }

        setUploadError((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
      }

      // Update local state with new audio data
      setLocalFeedbackItems((prev) =>
        prev.map((item) =>
          item.id === selectedFeedback.id
            ? {
                ...item,
                annotatedImage: submission.annotatedImage,
                originalImage: submission.originalImage,
                feedback: submission.feedback,
                hasAudio: submission.hasAudio,
                audioUrl: submission.audioUrl,
                audioBlob: submission.audioBlob,
                audioFilename: submission.audioFilename,
                timestamp: Date.now(),
              }
            : item,
        ),
      );

      // ✅ NEW: Silent refresh of card data in background after update
      if (cardId && uniqueId) {
        console.log(
          "🔄 Silently refreshing card data in background after unclear update:",
          cardId,
        );

        setTimeout(async () => {
          try {
            const accessToken = TokenManager.getAccessToken();
            if (!accessToken) return;

            const response = await fetch(
              API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  "Content-Type": "application/json",
                },
              },
            );

            if (response.ok) {
              const data = await response.json();
              console.log(
                "✅ Card data silently refreshed after unclear update:",
                data,
              );

              // Update status map with fresh data
              const statusMap: Record<
                string,
                "acknowledged" | "unclear" | "reshare"
              > = {};
              const unclearDetailsMap: Record<string, any> = {};
              const profileImagesMap: Record<string, string | null> = {};

              if (data.feedbacks && Array.isArray(data.feedbacks)) {
                data.feedbacks.forEach((feedback: any) => {
                  const feedbackId = feedback.feedback_id?.toString();
                  if (!feedbackId) return;

                  // Check for reshare status
                  if (
                    feedback.status &&
                    feedback.status.toLowerCase() === "reshare"
                  ) {
                    statusMap[feedbackId] = "reshare";
                    return;
                  }

                  // Process developer statuses
                  if (
                    feedback.developers &&
                    Array.isArray(feedback.developers)
                  ) {
                    const latestDeveloper = feedback.developers[0];

                    if (latestDeveloper?.status) {
                      const status = latestDeveloper.status.toLowerCase();

                      if (
                        status === "acknowledged" ||
                        status === "unclear" ||
                        status === "reshare"
                      ) {
                        statusMap[feedbackId] = status as
                          | "acknowledged"
                          | "unclear"
                          | "reshare";

                        if (status !== "reshare") {
                          profileImagesMap[feedbackId] =
                            latestDeveloper.profile_image || null;

                          if (status === "unclear") {
                            unclearDetailsMap[feedbackId] = {
                              email: latestDeveloper.developer_email || "",
                              status: "unclear",
                              unclear_reason:
                                latestDeveloper.unclear_reason || [],
                              comment: latestDeveloper.comment || "",
                              action_time: latestDeveloper.action_time || "",
                            };
                          }
                        }
                      }
                    }
                  }
                });
              }

              // Update states with fresh data
              setFeedbackStatuses(statusMap);
              setDeveloperProfileImages(profileImagesMap);
              window.__unclearDetailsCache = unclearDetailsMap;

              console.log(
                "✅ Card data silently updated after unclear feedback update",
              );
            }
          } catch (error) {
            console.warn(
              "⚠️ Silent card refresh failed (non-critical):",
              error,
            );
          }
        }, 1000); // 1 second delay to allow backend to process
      }

      // Force VoiceRecorder to re-render with new state
      setVoiceRecorderKey((prev) => {
        const newKey = prev + 1;
        console.log("🔄 Forcing VoiceRecorder re-render:", newKey);
        return newKey;
      });

      // Close editor
      setShowEditScreenshotPreview(false);
      setEditingScreenshotData(null);

      // Show success message
      showToastNotification("Screenshot updated successfully!", "success");

      console.log("✅ Screenshot updated in local state with audio:", {
        hasAudio: submission.hasAudio,
        audioUrl: submission.audioUrl ? "Present" : "None",
      });
    },
    [selectedFeedback, showToastNotification, cardId], // ⬅️ CHANGED: Added cardId dependency
  );

  const handleEditRecordingSubmit = useCallback(
    async (submission: RecordingSubmission) => {
      console.log("✏️ Recording edit submitted:", {
        id: submission.id,
        uniqueFeedbackId: submission.uniqueFeedbackId,
        duration: submission.duration,
      });

      if (!selectedFeedback) return;

      // Update local state immediately
      setLocalFeedbackItems((prev) =>
        prev.map((item) =>
          item.id === selectedFeedback.id
            ? {
                ...item,
                recordingBlob: submission.recordingBlob,
                dataUrl: submission.dataUrl,
                feedback: submission.feedback,
                duration: submission.duration,
                thumbnailUrl: submission.thumbnailUrl,
                timestamp: Date.now(),
              }
            : item,
        ),
      );

      // ✅ NEW: Silent refresh of card data in background after recording update
      if (cardId && selectedFeedback.uniqueFeedbackId) {
        console.log(
          "🔄 Silently refreshing card data in background after recording update:",
          cardId,
        );

        setTimeout(async () => {
          try {
            const accessToken = TokenManager.getAccessToken();
            if (!accessToken) return;

            const response = await fetch(
              API_URLS.GET_FULL_CARDS_FEEDBACK_STATUS_UPDATE(cardId),
              {
                method: "GET",
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  "Content-Type": "application/json",
                },
              },
            );

            if (response.ok) {
              const data = await response.json();
              console.log(
                "✅ Card data silently refreshed after recording update:",
                data,
              );

              // Update status map with fresh data
              const statusMap: Record<
                string,
                "acknowledged" | "unclear" | "reshare"
              > = {};
              const unclearDetailsMap: Record<string, any> = {};
              const profileImagesMap: Record<string, string | null> = {};
              const developerNamesMap: Record<string, string> = {};

              if (data.feedbacks && Array.isArray(data.feedbacks)) {
                data.feedbacks.forEach((feedback: any) => {
                  const feedbackId = feedback.feedback_id?.toString();
                  if (!feedbackId) return;

                  if (
                    feedback.status &&
                    feedback.status.toLowerCase() === "reshare"
                  ) {
                    statusMap[feedbackId] = "reshare";
                    return;
                  }

                  if (
                    feedback.developers &&
                    Array.isArray(feedback.developers)
                  ) {
                    const latestDeveloper = feedback.developers[0];

                    if (latestDeveloper?.status) {
                      const status = latestDeveloper.status.toLowerCase();

                      if (
                        status === "acknowledged" ||
                        status === "unclear" ||
                        status === "reshare"
                      ) {
                        statusMap[feedbackId] = status as
                          | "acknowledged"
                          | "unclear"
                          | "reshare";

                        if (status !== "reshare") {
                          profileImagesMap[feedbackId] =
                            latestDeveloper.profile_image || null;

                          //  Store developer name
                          if (
                            latestDeveloper.developer_name ||
                            latestDeveloper.developer_email
                          ) {
                            const displayName =
                              latestDeveloper.developer_name ||
                              latestDeveloper.developer_email.split("@")[0];
                            developerNamesMap[feedbackId] = displayName;
                          }

                          if (status === "unclear") {
                            unclearDetailsMap[feedbackId] = {
                              email: latestDeveloper.developer_email || "",
                              status: "unclear",
                              unclear_reason:
                                latestDeveloper.unclear_reason || [],
                              comment: latestDeveloper.comment || "",
                              action_time: latestDeveloper.action_time || "",
                            };
                          }
                        }
                      }
                    }
                  }
                });
              }

              setFeedbackStatuses(statusMap);
              setDeveloperProfileImages(profileImagesMap);
              setDeveloperNames(developerNamesMap);
              window.__unclearDetailsCache = unclearDetailsMap;

              console.log(
                "✅ Card data silently updated after recording update",
              );
            }
          } catch (error) {
            console.warn(
              "⚠️ Silent card refresh failed (non-critical):",
              error,
            );
          }
        }, 1000);
      }

      // Close editor
      setShowEditRecordingPreview(false);
      setEditingRecordingData(null);

      // Show success message
      showToastNotification("Recording updated successfully!", "success");

      console.log("✅ Recording updated in local state");
    },
    [selectedFeedback, showToastNotification, cardId], // ⬅️ CHANGED: Added cardId dependency
  );

  // Audio loading state handler
  const handleAudioLoadingChange = useCallback((loading: boolean) => {
    // Handle loading state changes if needed
  }, []);

  const toggleDropdown = useCallback(() => {
    console.log("🔘 Toggle dropdown clicked:", {
      currentState: isDropdownOpen,
      newState: !isDropdownOpen,
    });
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  const handleDeleteClick = useCallback(() => {
    console.log("🗑️ Delete clicked from dropdown");
    setIsDropdownOpen(false); // ✅ Close dropdown first
    setShowDeleteConfirm(true); // Then show confirm
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedFeedback) return;

    console.log("🗑️ Confirming delete:", {
      id: selectedFeedback.id,
      type: selectedFeedback.type,
      uniqueFeedbackId: selectedFeedback.uniqueFeedbackId,
    });

    // ✅ Close popup immediately
    setShowDeleteConfirm(false);

    // Close unclear drawer if open
    setShowUnclearDrawer(false);
    setUnclearFeedbackData(null);

    let deletionSuccessful = false;

    // Delete from backend if we have a unique feedback ID
    if (selectedFeedback.uniqueFeedbackId) {
      try {
        const accessToken = TokenManager.getAccessToken();
        if (!accessToken) {
          console.error("No access token available for deletion");
          showToastNotification("Unable to delete: No access token", "error");
          return;
        }

        // Extract numeric ID from feedback ID
        const numericFeedbackId = String(selectedFeedback.uniqueFeedbackId)
          .replace(/^feedback_/, "")
          .trim();

        console.log("🗑️ Deleting from database:", numericFeedbackId);

        const response = await fetch(API_URLS.DELETE_FILE(numericFeedbackId), {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(
            "Failed to delete from database:",
            response.status,
            errorText,
          );
          showToastNotification(
            `Failed to delete feedback: ${errorText}`,
            "error",
          );
          return;
        } else {
          console.log("✅ Successfully deleted from database");
          deletionSuccessful = true;
        }
      } catch (error) {
        console.error("❌ Error deleting from database:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        showToastNotification(
          `Failed to delete feedback: ${errorMessage}`,
          "error",
        );
        return;
      }
    }

    // Only update local state if deletion was successful
    if (deletionSuccessful || !selectedFeedback.uniqueFeedbackId) {
      // Update local state to remove the feedback item
      const updatedItems = localFeedbackItems.filter(
        (item) => item.id !== selectedFeedback.id,
      );
      setLocalFeedbackItems(updatedItems);

      // Clear audio cache for this feedback
      if (selectedFeedback.uniqueFeedbackId) {
        const uniqueId = selectedFeedback.uniqueFeedbackId;

        audioCheckCacheRef.current.delete(uniqueId);
        setAudioExistsMap((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
        setForceRecorderMode((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
        setUploadError((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
        setIsUploadingAudio((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });

        // Clear from feedback statuses
        setFeedbackStatuses((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });

        // Clear from unclear details cache
        if (window.__unclearDetailsCache) {
          delete window.__unclearDetailsCache[uniqueId];
        }

        // Clear from fetched details ref
        fetchedUnclearDetailsRef.current.delete(uniqueId);

        // Clear from developer profile images
        setDeveloperProfileImages((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });

        // Clear from developer names
        setDeveloperNames((prev) => {
          const updated = { ...prev };
          delete updated[uniqueId];
          return updated;
        });
      }

      // Select the first remaining feedback item or null
      const nextSelectedId =
        updatedItems.length > 0 ? updatedItems[0].id : null;
      setSelectedFeedbackId(nextSelectedId);

      // Call the parent callback to delete from backend
      onDeleteFeedback?.(selectedFeedback.id);

      // Show success message
      showToastNotification("Feedback deleted successfully", "success");

      console.log("✅ Feedback item removed from local state and caches");
    }
  }, [
    selectedFeedback,
    localFeedbackItems,
    onDeleteFeedback,
    showToastNotification,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
  }, []);

  const handleVolumeSliderMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      console.log("🔊 Volume slider mousedown");

      const updateVolume = (clientY: number) => {
        if (!volumeSliderRef.current) {
          console.log("🔊 No volume slider ref");
          return;
        }

        const rect = volumeSliderRef.current.getBoundingClientRect();
        const y = clientY - rect.top;
        const percentage = Math.max(0, Math.min(1, 1 - y / rect.height));

        console.log("🔊 Updating volume:", {
          percentage,
          y,
          height: rect.height,
        });

        setVideoVolume(percentage);

        const video = document.querySelector("video") as HTMLVideoElement;
        if (video) {
          video.volume = percentage;
          if (percentage === 0) {
            video.muted = true;
            setIsVideoMuted(true);
          } else if (isVideoMuted) {
            video.muted = false;
            setIsVideoMuted(false);
          }

          console.log("🔊 Video volume updated:", {
            percentage,
            videoVolume: video.volume,
            videoMuted: video.muted,
          });
        } else {
          console.error(
            "🔊 ERROR: No video element found during volume update!",
          );
        }
      };

      updateVolume(e.clientY);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        updateVolume(moveEvent.clientY);
      };

      const handleMouseUp = () => {
        console.log("🔊 Volume slider mouse up");
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };

      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [isVideoMuted],
  );

  // Render feedback item with better audio indicators
  const renderFeedbackItem = useCallback(
    (item: FeedbackItem, index: number) => {
      const itemStatus =
        isSubmittedMode &&
        showDeveloperStatus &&
        item.uniqueFeedbackId &&
        feedbackStatuses[item.uniqueFeedbackId]
          ? feedbackStatuses[item.uniqueFeedbackId]
          : null;

      const previewText = stripHtmlTags(item.feedback || item.question || "");

      return (
        <div
          key={item.id}
          data-feedback-id={item.id}
          ref={(el) => {
            if (el) {
              feedbackItemRefs.current.set(item.id, el);
            } else {
              feedbackItemRefs.current.delete(item.id);
            }
          }}
          onClick={() => handleFeedbackSelect(item.id)}
          className={`p-3 w-full h-28 3xl:h-32 cursor-pointer transition-all relative border-b-[0.5px] border-[#D5D9EA] ${
            selectedFeedbackId === item.id
              ? "bg-[#F3FAFF] border-l-4 border-l-[#49AFFF]"
              : "bg-white hover:bg-gray-50 border-l border-l-transparent"
          }`}
        >
          <div className="flex items-start justify-center gap-3">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={profileImage}
                alt="User profile"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1 min-w-0 pr-8">
              {previewText ? (
                <p
                  className="text-[12px] 2xl:text-[14px] 3xl:text-[16px] text-[#1E1F26] font-medium mb-2 line-clamp-2"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {previewText}
                </p>
              ) : (
                <p
                  className="text-[12px] 2xl:text-[14px] text-[#1E1F26] mb-2 italic"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  No feedback provided
                </p>
              )}
            </div>

            <div className="absolute bottom-3 left-3 flex items-center gap-7">
              <span
                className="text-xs 3xl:text-[14px] text-[#1E1F26] font-medium"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {String(index + 1).padStart(2, "0")}
              </span>

              {/* ✅ UPDATED: Show status with conditional profile picture */}
              {itemStatus && (
                <div className="flex items-center gap-2">
                  {/* ✅ UPDATED: Show profile picture for acknowledged/unclear statuses */}
                  {itemStatus !== "reshare" && item.uniqueFeedbackId && (
                    <div
                      className="relative"
                      onMouseEnter={() => {
                        if (!item.uniqueFeedbackId) return;
                        console.log("🔍 Mouse enter on profile picture:", {
                          feedbackId: item.uniqueFeedbackId,
                          hasProfileImage:
                            !!developerProfileImages[item.uniqueFeedbackId],
                          hasDeveloperName:
                            !!developerNames[item.uniqueFeedbackId],
                          developerName: developerNames[item.uniqueFeedbackId],
                        });
                        setHoveredDeveloperFeedback(
                          item.uniqueFeedbackId || null,
                        );
                      }}
                      onMouseLeave={() => {
                        console.log("🔍 Mouse leave on profile picture");
                        setHoveredDeveloperFeedback(null);
                      }}
                    >
                      <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border border-gray-200 cursor-pointer">
                        <img
                          src={
                            developerProfileImages[item.uniqueFeedbackId]
                              ? `${BASE_URL}${
                                  developerProfileImages[item.uniqueFeedbackId]
                                }`
                              : AvatarIcon
                          }
                          alt="Developer"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = AvatarIcon;
                          }}
                        />
                      </div>

                      {/* 🆕 Developer Name Tooltip - RIGHT SIDE */}
                      {(() => {
                        if (!item.uniqueFeedbackId) return null;

                        const shouldShow =
                          hoveredDeveloperFeedback === item.uniqueFeedbackId &&
                          developerNames[item.uniqueFeedbackId];

                        console.log("🔍 Tooltip render check:", {
                          shouldShow,
                          hoveredId: hoveredDeveloperFeedback,
                          itemId: item.uniqueFeedbackId,
                          hasName: !!developerNames[item.uniqueFeedbackId],
                          name: developerNames[item.uniqueFeedbackId],
                        });

                        return shouldShow ? (
                          <div className="absolute left-full top-1/2 ml-2 z-50 animate-tooltip-right">
                            <div className="relative bg-white text-[#1E1F26] text-[14px] font-medium px-3 py-2 rounded-[8px] border border-[#E4E6EE] shadow-lg whitespace-nowrap flex items-center gap-2 -translate-y-1/2">
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-200">
                                <img
                                  src={`${BASE_URL}${
                                    developerProfileImages[
                                      item.uniqueFeedbackId
                                    ]
                                  }`}
                                  alt={developerNames[item.uniqueFeedbackId]}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.src =
                                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23D1D5DB'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";
                                  }}
                                />
                              </div>
                              <span>
                                {developerNames[item.uniqueFeedbackId]}
                              </span>
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* Status text */}
                  <div
                    className={`text-[12px] 4xl:text-[14px] font-medium ${
                      itemStatus === "acknowledged"
                        ? "text-[#28B446]"
                        : itemStatus === "unclear"
                          ? "text-[#DE860B]"
                          : "text-[#0E73F6]"
                    }`}
                  >
                    {itemStatus === "acknowledged"
                      ? "Acknowledged"
                      : itemStatus === "unclear"
                        ? "Unclear"
                        : "Clarified feedback"}
                  </div>
                </div>
              )}
            </div>

            <div className="absolute bottom-3 right-3 flex items-center gap-1">
              {item.type === "screenshot" && item.uniqueFeedbackId && (
                <>
                  {audioExistsMap[item.uniqueFeedbackId] === true && (
                    <Audio className="w-5 h-5" />
                  )}
                  {(audioExistsMap[item.uniqueFeedbackId] === "loading" ||
                    isUploadingAudio[item.uniqueFeedbackId]) && (
                    <div className="w-3 h-3 animate-spin">
                      <svg
                        className="w-full h-full text-blue-500"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    </div>
                  )}
                </>
              )}
              {item.type === "screenshot" ? (
                <Screenshot className="w-5 h-5" />
              ) : (
                <Video className="w-5 h-5" />
              )}
            </div>
          </div>
        </div>
      );
    },
    [
      selectedFeedbackId,
      audioExistsMap,
      isUploadingAudio,
      handleFeedbackSelect,
      isSubmittedMode,
      showDeveloperStatus,
      feedbackStatuses,
      developerProfileImages,
      developerNames, // 🆕 NEW: Added dependency
      hoveredDeveloperFeedback, // 🆕 NEW: Added dependency
    ],
  );

  const renderVideoControls = useCallback(
    () => (
      <div className="flex items-center justify-center mt-4">
        <div className="flex w-full items-center space-x-2 bg-[#F4F5F6] rounded-[8px] px-4 py-[6px] 3xl:py-2 shadow-sm">
          {/* Play/Pause Button */}
          <button
            onClick={toggleVideoPlayPause}
            className="flex items-center justify-center transition-colors"
          >
            {isVideoPlaying ? (
              <Pause className="w-5 h-5 text-[#1E1F26]" />
            ) : (
              <Play className="w-5 h-5 text-[#1E1F26] ml-0.5" />
            )}
          </button>

          {/* Skip buttons */}
          <button
            onClick={skipBackward}
            className="w-8 h-8 flex items-center justify-center text-[#1E1F26]"
          >
            <SkipBackward className="w-5 h-5" />
          </button>

          <button
            onClick={skipForward}
            className="w-8 h-8 flex items-center justify-center text-[#1E1F26]"
          >
            <SkipForward className="w-5 h-5" />
          </button>

          {/* Progress Bar - KEY FIX */}
          <div className="flex-1 mx-4  relative">
            <div
              className="h-1 bg-gray-300 rounded-full cursor-pointer relative"
              onClick={handleProgressBarClick}
            >
              <div
              className="h-1 bg-[#49AFFF] rounded-full"  
                style={{
                  width: `${progressPercentage}%`,
                  transition: isVideoPlaying ? "none" : "width 0.1s ease-out",
                }}
              />
              <div
                className="  absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#49AFFF] border-4 border-[#D3E2EB] border-opacity-100 rounded-full shadow-sm"
                style={{
                  left: `${progressPercentage}%`,
                  marginLeft: "-6px",
                  transition: isVideoPlaying ? "none" : "left 0.1s ease-out",
                }}
              />
            </div>
          </div>

          {/* Volume Control */}
          <div className="flex items-center space-x-2 relative" ref={volumeRef}>
            <button
              onClick={toggleVolumeSlider}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800"
            >
              {isVideoMuted || videoVolume === 0 ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>

            {showVolumeSlider && (
              <div className="absolute bottom-full mb-2 -right-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[40px] z-10">
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-gray-600 font-medium">
                    {Math.round(videoVolume * 100)}
                  </div>
                  <div
                    ref={volumeSliderRef}
                    className="relative h-20 w-6 flex items-center justify-center cursor-pointer select-none"
                    onMouseDown={handleVolumeSliderMouseDown}
                  >
                    <div className="w-1 h-full bg-gray-300 rounded-full relative">
                      <div
                        className="absolute bottom-0 w-full bg-[#1E1F26] rounded-full transition-all duration-75 ease-out"
                        style={{ height: `${videoVolume * 100}%` }}
                      />
                    </div>
                    <div
                      className="absolute w-3 h-3 bg-[#1E1F26] rounded-full shadow-sm pointer-events-none transition-all duration-75 ease-out hover:scale-110"
                      style={{
                        bottom: `${videoVolume * 100}%`,
                        left: "50%",
                        transform: "translateX(-50%) translateY(50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      toggleVideoPlayPause,
      isVideoPlaying,
      skipBackward,
      skipForward,
      handleProgressBarClick,
      progressPercentage,
      toggleVolumeSlider,
      isVideoMuted,
      videoVolume,
      showVolumeSlider,
      handleVolumeSliderMouseDown,
    ],
  );
  // Render audio section with upload state handling for MP3
  const renderAudioSection = useCallback(() => {
    if (
      !selectedFeedback ||
      selectedFeedback.type !== "screenshot" ||
      !selectedFeedback.uniqueFeedbackId
    ) {
      return null;
    }

    const uniqueId = selectedFeedback.uniqueFeedbackId;
    const audioState = audioExistsMap[uniqueId];
    const isForceRecorder = forceRecorderMode[uniqueId];
    const isUploading = isUploadingAudio[uniqueId];
    const error = uploadError[uniqueId];

    console.log("renderAudioSection - Current state:", {
      feedbackId: selectedFeedback.id,
      uniqueFeedbackId: uniqueId,
      audioState,
      isForceRecorder,
      isUploading,
      error,
      voiceRecorderKey,
    });

    // Show uploading state
    if (isUploading) {
      return (
        <div className="px-6 py-4 mt-auto">
          <div className="flex items-center justify-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 animate-spin">
                <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <span className="text-sm text-blue-700">
                Converting to MP3 and uploading...
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Show upload error with retry option
    if (error) {
      return (
        <div className="px-6 py-4 mt-auto">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-4 h-4 text-red-500">⚠️</div>
              <span className="text-sm text-red-700 font-medium">
                MP3 Upload Failed
              </span>
            </div>
            <p className="text-sm text-red-600 mb-2">{error}</p>
          </div>
          <VoiceNoteRecorder
            key={`voice-recorder-error-${selectedFeedback.id}-${voiceRecorderKey}`}
            initialState="idle"
            onRecordingComplete={handleRecordingComplete}
            disabled={false}
          />
        </div>
      );
    }

    // Show recorder if forced or no audio exists
    if (!isSubmittedMode && (isForceRecorder || audioState === false)) {
      return (
        <div className="px-6 py-4  bg-white">
          <VoiceNoteRecorder
            key={`voice-recorder-${selectedFeedback.id}-${voiceRecorderKey}`}
            initialState="idle"
            onRecordingComplete={handleRecordingComplete}
            disabled={false}
          />
        </div>
      );
    }

    // Show player if we know there's audio
    if (audioState === true) {
      return (
        <div className="px-6 py-4 mt-auto">
          <AudioPlayer
            feedbackId={uniqueId}
            onError={handleAudioPlayerError}
            onLoadingChange={handleAudioLoadingChange}
            onDelete={
              isSubmittedMode
                ? undefined
                : () => handleAudioDelete(selectedFeedback.id, uniqueId)
            }
            className="w-full"
          />
        </div>
      );
    }

    // ✅ NEW: Dummy audio player when no audio is available in submitted mode
    if (isSubmittedMode) {
      return (
        <div className="px-6 py-4 mt-auto">
          <div className="w-full">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-10 xl:h-12 3xl:h-14 4xl:h-16 bg-[#F4F5F6] rounded-[8px] flex items-center px-3 overflow-hidden relative opacity-50">
                {/* Clickable Play Button - NOW SHOWS ERROR ON CLICK */}
                <button
                  onClick={handleNoAudioClick}
                  className="text-gray-400 flex items-center justify-center mr-4 cursor-pointer hover:text-gray-500 transition-colors"
                  aria-label="No audio available"
                >
                  <PlayRecording className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 ml-0.5" />
                </button>

                {/* Empty Progress Bar */}
                <div className="flex-1 relative flex items-center">
                  <div className="flex-1 h-1 bg-gray-300 rounded-full relative">
                    <div
                      className="h-1 bg-gray-400 rounded-full"
                      style={{ width: "0%" }}
                    />
                  </div>
                </div>

                {/* "No Audio" Text Overlay */}
                {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-xs 3xl:text-sm text-gray-500 font-medium">
                No audio available
              </span>
            </div> */}
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Default to recorder when state is unknown
    console.log("Showing VoiceNoteRecorder (default - unknown state)");
    return (
      <div className="p-4 mt-auto">
        <VoiceNoteRecorder
          key={`voice-recorder-${selectedFeedback.id}-${voiceRecorderKey}`}
          initialState="idle"
          onRecordingComplete={handleRecordingComplete}
          disabled={false}
        />
      </div>
    );
  }, [
    selectedFeedback,
    audioExistsMap,
    forceRecorderMode,
    isUploadingAudio,
    uploadError,
    voiceRecorderKey,
    isSubmittedMode,
    handleRecordingComplete,
    handleAudioPlayerError,
    handleAudioLoadingChange,
    handleAudioDelete,
  ]);

  const renderFeedbackContent = useCallback(
    (feedback: FeedbackItem | null, isExiting: boolean = false) => {
      // Force re-render by accessing drawerExpandedState
      const _ = drawerExpandedState;

      if (!feedback) {
        return (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                <Camera className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500">Select a feedback item to preview</p>
            </div>
          </div>
        );
      }

      // Check if this feedback has unclear status
      const hasUnclearStatus =
        !isExiting &&
        feedback.id === selectedFeedbackId &&
        isSubmittedMode &&
        showDeveloperStatus &&
        feedback.uniqueFeedbackId &&
        feedbackStatuses[feedback.uniqueFeedbackId] === "unclear" &&
        showUnclearDrawer &&
        unclearFeedbackData;

      return (
        <div className="h-full flex flex-col relative">
          {/* Main content wrapper */}
          <div
            className={`h-full flex flex-col ${
              hasUnclearStatus ? "pointer-events-none bg-[#1A1C2859] " : ""
            }`}
          >
            {/* Preview Header */}
            {(feedback.feedback || feedback.question) && (
              <div className="px-6 pt-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <div className="relative">
                      <div
                        ref={(el) => {
                          if (el && feedback.id) {
                            textContentRefs.current.set(feedback.id, el);
                          }
                        }}
                        className="text-base font-medium text-[#11142D] prose prose-sm max-w-none relative"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(feedback.feedback || feedback.question || ""),
                        }}
                        style={{
                          fontFamily: DEFAULT_FONT_FAMILY,
                          lineHeight: "1.6",
                          display: "-webkit-box",
                          WebkitLineClamp: textOverflowMap[feedback.id]
                            ? 1
                            : 999,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          maxHeight: textOverflowMap[feedback.id]
                            ? "2.4em"
                            : "none",
                        }}
                      />
                      {textOverflowMap[feedback.id] && (
                        <div className="inline">
                          <span className="text-[#11142D]">... </span>
                          <button
                            onClick={handleShowMoreClick}
                            className="text-[#4361EE] text-sm font-medium hover:underline inline"
                          >
                            Show more
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {!isSubmittedMode &&
                    !isExiting &&
                    feedback.id === selectedFeedbackId && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEditFeedback(feedback.id)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit feedback"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <div className="relative" ref={dropdownRef}>
                          <button
                            onClick={toggleDropdown}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="More options"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {isDropdownOpen && (
                            <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                              <div className="p-1">
                                <button
                                  onClick={handleDeleteClick}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#41444E] rounded-md transition-colors"
                                >
                                  <DeleteIcon className="w-4 h-4" />
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}

            {/* Preview Content */}
            <div className="flex-1 px-6 py-3 overflow-auto flex flex-col">
              <div className="flex-1 flex items-center justify-center rounded-[14px] min-h-0">
                {feedback.type === "screenshot" ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <img
                      src={feedback.annotatedImage}
                      alt="Screenshot preview"
                      className="max-w-full max-h-full object-contain rounded-[14px]"
                    />
                  </div>
                ) : feedback.type === "recording" ? (
                  <div className="w-full h-full flex items-center justify-center rounded-[8px]">
                    <div
                      className="relative w-full h-full flex items-center justify-center rounded-[14px]"
                      style={{ maxHeight: "calc(100vh)" }}
                    >
                      <div className="relative w-full h-full flex items-center justify-center rounded-[14px]">
                        <div className="absolute inset-0 w-full flex items-center justify-center rounded-[14px]">
                          {videoUrl && isValidVideoSource(videoUrl) ? (
                            <video
                              ref={videoRef}
                              src={videoUrl || ""}
                              className="w-full h-full object-contain rounded-[14px] bg-white"
                              onPlay={handleVideoPlay}
                              onPause={handleVideoPause}
                              onEnded={handleVideoEnded}
                              onError={handleVideoError}
                              preload="metadata"
                              playsInline
                              controls={false}
                            />
                          ) : !videoError && !selectedFeedback?.recordingBlob ? (
                            // Shimmer while blob is still loading from background fetch
                            <div
                              className="w-full h-full rounded-[14px]"
                              style={{
                                background:
                                  "linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.5s ease-in-out infinite",
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white bg-gray-800">
                              <div className="text-center">
                                <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-sm">
                                  {videoError || "Video source not available"}
                                </p>
                              </div>
                            </div>
                          )}

                          {videoUrl &&
                            isValidVideoSource(videoUrl) &&
                            !isVideoPlaying && (
                              <div
                                className="absolute inset-0 flex items-center justify-center cursor-pointer rounded-[8px] hover:bg-black/30 transition-colors z-10"
                                onClick={toggleVideoPlayPause}
                              >
                                <VideoPlay className="w-32 h-32" />
                              </div>
                            )}
                          {videoUrl &&
                            isValidVideoSource(videoUrl) &&
                            isVideoPlaying && (
                              <div
                                className="absolute inset-0 flex items-center justify-center cursor-pointer rounded-[8px] hover:bg-black/30 transition-colors opacity-0 hover:opacity-100 z-10"
                                onClick={toggleVideoPlayPause}
                              >
                                <VideoPause className="w-32 h-32" />
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Video className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <p>No video data available</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Video Controls */}
              {feedback.type === "recording" &&
                videoUrl &&
                isValidVideoSource(videoUrl) && (
                  <div className="mt-auto">{renderVideoControls()}</div>
                )}
            </div>

            {/* Audio Section for Screenshots */}
            {!isExiting &&
              feedback.id === selectedFeedbackId &&
              renderAudioSection()}
          </div>

          {/* Unclear Drawer */}
          {hasUnclearStatus && (
            <div className="absolute bottom-0 left-0 right-0 z-50">
              <div className="bg-white border-t border-[#1E1F26] rounded-t-[10px]">
                {/* Dropdown Button */}
                <div className="absolute -top-10 right-6 z-50">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (!feedback?.uniqueFeedbackId) return;

                      setDrawerExpandedState((prev) => {
                        const currentExpanded =
                          prev[feedback.uniqueFeedbackId!] ?? true;
                        const newState = !currentExpanded;

                        return {
                          ...prev,
                          [feedback.uniqueFeedbackId!]: newState,
                        };
                      });
                    }}
                    className="cursor-pointer select-none transition-transform duration-300 ease-out"
                    type="button"
                  >
                    {(() => {
                      const uniqueId = feedback?.uniqueFeedbackId;
                      const isExpanded = uniqueId
                        ? (drawerExpandedState[uniqueId] ?? true)
                        : true;
                      return isExpanded ? (
                        <UnclearUpIcon className="w-8 h-8 border-[0.5px] border-[#B7BBCA] rounded-[10px]" />
                      ) : (
                        <UnclearDownIcon className="w-8 h-8 border-[0.5px] border-[#B7BBCA] rounded-[10px]" />
                      );
                    })()}
                  </button>
                </div>

                {/* Header - Always visible */}
                <div className="px-6 pb-4 pt-6">
                  <div className="flex items-center justify-between">
                    {/* Left side - Icon, Title, and Subtitle */}
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <UnclearIcon className="w-4 h-4 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6" />
                        <h3
                          className="text-[22px] 3xl:text-[24px] font-semibold text-[#1E1F26]"
                          style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                        >
                          Unclear feedback
                        </h3>
                      </div>
                      <p
                        className="text-[14px] text-[#61646F]"
                        style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                      >
                        This feedback is marked as unclear from developer
                      </p>
                    </div>

                    {/* Right side - Button when collapsed */}
                    {(() => {
                      const uniqueId = feedback?.uniqueFeedbackId;
                      const isExpanded = uniqueId
                        ? (drawerExpandedState[uniqueId] ?? true)
                        : true;
                      return !isExpanded ? (
                        <Button
                          variant="primary"
                          className="px-6 py-2.5 text-sm flex-shrink-0"
                          onClick={handleNotedAndUpdate}
                        >
                          Update Feedback
                        </Button>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Expandable Content */}
                <div
                  className={`overflow-hidden transition-all duration-300 ease-out ${(() => {
                    const uniqueId = feedback?.uniqueFeedbackId;
                    const isExpanded = uniqueId
                      ? (drawerExpandedState[uniqueId] ?? true)
                      : true;
                    return isExpanded
                      ? "max-h-[500px] opacity-100"
                      : "max-h-0 opacity-0";
                  })()}`}
                >
                  <div className="px-6 pb-6">
                    <div className="bg-[#FFFCF8] border-[0.5px] border-[#FF9500] rounded-[10px] px-6 py-4 mb-6">
                      {unclearFeedbackData?.unclear_reason?.[0] ===
                      "Loading details..." ? (
                        <div className="flex items-center justify-center py-2">
                          <div className="flex items-center gap-2 text-[#DE860B]">
                            <div className="w-4 h-4 animate-spin">
                              <svg
                                className="w-full h-full"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            </div>
                            <span className="text-sm">Loading details...</span>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Tags Row */}
                          <div className="flex flex-wrap gap-2 ">
                            {unclearFeedbackData?.unclear_reason.map(
                              (reason, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1.5 bg-[#FFEAC2] text-[#1E1F26] rounded-lg text-sm 3xl:text-[16px] font-medium border border-[#FDC357]"
                                >
                                  {reason}
                                </span>
                              ),
                            )}
                          </div>

                          {/* Comment Text */}
                          {unclearFeedbackData?.comment &&
                            unclearFeedbackData.comment.trim() !== "" && (
                              <div className="flex flex-col  text-sm 3xl:text-[16px] text-[#1E1F26] mt-3">
                                <span className="text-sm 3xl:text-[16px] font-medium">
                                  Comment:
                                </span>
                                <span>{unclearFeedbackData.comment}</span>
                              </div>
                            )}
                        </>
                      )}
                    </div>

                    {/* Separator */}
                    <div className="border-t border-gray-200 mb-4" />

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between">
                      {/* Delete Button */}
                      <button
                        onClick={handleDeleteClick}
                        className="flex items-center gap-2"
                      >
                        <DeleteIcon className="w-5 h-5 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 4xl:w-8 4xl:h-8" />
                      </button>

                      {/* Acknowledge & fix Button */}
                      <Button
                        variant="primary"
                        className="px-6 py-2.5 text-sm"
                        onClick={handleNotedAndUpdate}
                      >
                        Update Feedback
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    },
    [
      selectedFeedbackId,
      selectedFeedback?.uniqueFeedbackId,
      videoUrl,
      isVideoPlaying,
      videoError,
      handleVideoLoadedMetadata,
      handleVideoTimeUpdate,
      handleVideoEnded,
      handleVideoPlay,
      handleVideoPause,
      handleVideoError,
      setVideoError,
      setVideoDuration,
      toggleVideoPlayPause,
      renderVideoControls,
      renderAudioSection,
      showDeveloperStatus,
      feedbackStatuses,
      showUnclearDrawer,
      unclearFeedbackData,
      handleEditFeedback,
      toggleDropdown,
      isDropdownOpen,
      handleDeleteClick,
      isSubmittedMode,
      drawerExpandedState, // CRITICAL: Triggers re-render on drawer state change
    ],
  );
  return (
    <div className="min-h-screen bg-white ">
      {/* 🆕 NEW: Toast Notification */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 max-w-md animate-slide-in">
          <div
            className={`${
              toastType === "success"
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200"
            } border rounded-lg p-4 shadow-lg`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {toastType === "success" ? (
                  <svg
                    className="h-5 w-5 text-green-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5 text-red-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p
                  className={`text-sm font-medium ${
                    toastType === "success" ? "text-green-800" : "text-red-800"
                  }`}
                >
                  {toastType === "success" ? "Success" : "Error"}
                </p>
                <p
                  className={`text-sm mt-1 ${
                    toastType === "success" ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {toastMessage}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowToast(false)}
                  className={`${
                    toastType === "success"
                      ? "bg-green-50 text-green-400 hover:text-green-500"
                      : "bg-red-50 text-red-400 hover:text-red-500"
                  } rounded-md inline-flex focus:outline-none`}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Audio Error Message Notification */}
      {showAudioError && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-orange-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-orange-800">
                  Audio Not Available
                </p>
                <p className="text-sm text-orange-700 mt-1">
                  {audioErrorMessage}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setShowAudioError(false)}
                  className="bg-orange-50 rounded-md inline-flex text-orange-400 hover:text-orange-500 focus:outline-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        {/* Header */}
        <header className="bg-white px-6 pt-4 pb-2">
          {/* Back button - shown in submitted mode or when navigating from draft */}
          {(isSubmittedMode || showBackButton) && (
            <button
              onClick={onGoBackToDraft ?? handleBackToDashboard}
              className="flex items-center gap-1  text-[#1E1F26] transition-colors"
              title="Back to dashboard"
            >
              <ArrowLeft className="w-3 h-3" />
              <span className="text-xs 4xl:text-sm font-medium">Back</span>
            </button>
          )}

          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 flex-1">
              <h1
                className="text-xl 3xl:text-2xl font-medium text-[#1E1F26]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {feedbackName}
              </h1>
              {cardId && isSubmittedMode && (
                <DeveloperAvatarsGroup cardId={cardId} />
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Show different buttons based on feedback submission state */}
              {!feedbackSubmitted ? (
                <>
                  {/* Continue editing button */}
                  <Button
                    variant="cancel"
                    size="md"
                    onClick={handleContinueEditing}
                    className={`font-normal text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl  px-2 sm:px-3 xl:px-6 2xl:px-5 4xl:px-6 py-1 sm:py-1 xl:py-2.5 2xl:py-2 4xl:py-2.5`}
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Add more 
                  </Button>
                  {/* Submit feedback button */}
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmitFeedbackClick}
                    className={`font-normal text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl  px-2 sm:px-3 xl:px-8 2xl:px-7 4xl:px-8 py-1 sm:py-1 xl:py-2.5 2xl:py-2 4xl:py-2.5`}
                  >
                    Share
                  </Button>
                </>
              ) : (
                /* Share with developer button - shown after submission */
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleShareWithDeveloper}
                  disabled={!cardId}
                  className="flex items-center gap-2 justify-center px-2 sm:px-3 xl:px-8 2xl:px-7 4xl:px-8 py-1 sm:py-1 xl:py-2.5 2xl:py-2 4xl:py-2.5"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {isSharing ? (
                    <span
                      className="inline-block relative"
                      style={{ width: 60, height: 20 }}
                    >
                      <Lottie
                        animationData={DotLoader}
                        loop={true}
                        style={{
                          width: 120,
                          height: 48,
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </span>
                  ) : (
                    <>
                      
                     
                      <span className="text-xs sm:text-sm xl:text-sm 2xl:text-base 4xl:text-xl font-normal">
                        {hasSharedOnce ? "Share" : "Share"}
                      </span>
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </header>
        {/* Content Area */}
        <div className="flex h-[calc(100vh-100px)]">
          {/* Left Sidebar */}
          <div className="w-[27%] min-w-[240px] max-w-[540px] bg-white">
            <div className="pl-6 pr-2 mb-1">
              {isSearchOpen ? (
                <div className="flex items-center gap-1" ref={searchInputRef}>
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search feedbacks..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-4 py-1 border-[0.5px] font-inter border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => {
                      setIsSearchOpen(false);
                      setSearchQuery("");
                    }}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <h2
                    className="text-base 2xl:text-lg  font-medium text-[#1E1F26]"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    All feedbacks ({filteredFeedbackItems.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSearchClick}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                    <div className="relative" ref={filterRef}>
                      <button
                        onClick={handleFilterClick}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Filter className="w-4 h-4" />
                      </button>
                      {isFilterOpen && (
                        <div className="absolute right-0 top-full mt-2 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                          <div className="p-2 space-y-1">
                            {[
                              { value: "all", label: "All" },
                              { value: "screenshot", label: "Screenshots" },
                              {
                                value: "recording",
                                label: "Screen Recordings",
                              },
                              // ✅ NEW: Conditionally show status filters only in submitted mode
                              ...(isSubmittedMode && showDeveloperStatus
                                ? [
                                    {
                                      value: "acknowledged",
                                      label: "Acknowledged",
                                    },
                                    { value: "unclear", label: "Unclear" },
                                    {
                                      value: "reshare",
                                      label: "Clarified feedback",
                                    },
                                    { value: "not_read", label: "Not Read" },
                                  ]
                                : []),
                            ].map((filter) => (
                              <button
                                key={filter.value}
                                onClick={() =>
                                  handleFilterSelect(filter.value as any)
                                }
                                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                                  selectedFilter === filter.value
                                    ? "bg-[#F8F9FC] text-[#050505]"
                                    : "text-gray-700 hover:bg-[#F8F9FC]"
                                }`}
                              >
                                {filter.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className="mt-2 ml-4 mr-2 h-full rounded-[12px] bg-[#F7F7F7] overflow-y-auto custom-scrollbar-overlay"
              style={{ maxHeight: "calc(100vh - 140px )" }}
              ref={feedbackListRef}
            >
              {filteredFeedbackItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Screenshot className="w-6 h-6" />
                  </div>
                  <p className="text-sm">
                    {searchQuery
                      ? "No feedback items match your search"
                      : "No feedback items yet"}
                  </p>
                </div>
              ) : (
                <div
                  className={
                    isListOverflowing
                      ? isMac
                        ? "mx-[10px] my-[10px]"
                        : "ml-[10px] my-[10px]"
                      : "mx-[10px] my-[10px]"
                  }
                >
                  {/* ✅ RENDER EXISTING FEEDBACK ITEMS */}
                  {filteredFeedbackItems.map(renderFeedbackItem)}

                  {/* ✅ NEW: RENDER UPLOAD QUEUE ITEMS INLINE */}
                  {cardId && (
                    <UploadQueueIndicator
                      cardId={cardId}
                      variant="sidebar"
                      showCompleted={true}
                      onComplete={(completedItems) => {
                        console.log(
                          "✅ Uploads completed in ViewListFeedback:",
                          completedItems,
                        );

                        // Trigger refresh after uploads complete
                        setShouldRefreshAfterUpload(true);

                        // Clean up after triggering refresh
                        setTimeout(() => {
                          completedItems.forEach((item) => {
                            UploadQueueManager.removeItem(item.id);
                          });
                        }, 1000);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Content Area */}
          <div
            ref={contentContainerRef}
            className="flex-1 mt-10 ml-2 mr-4 bg-white border border-[#D2EEFF] rounded-[10px] shadow-sm overflow-hidden relative"
          >
            {/* Previous card - sliding out */}
            {isAnimating && previousFeedback && (
              <div
                className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
                  scrollDirection === "down"
                    ? "-translate-y-full"
                    : "translate-y-full"
                }`}
                style={{ zIndex: 1 }}
              >
                {renderFeedbackContent(previousFeedback, true)}
              </div>
            )}

            {/* Current card - sliding in */}
            <div
              className={`absolute inset-0 bg-white transition-transform duration-300 ease-out ${
                isAnimating
                  ? scrollDirection === "down"
                    ? "translate-y-0"
                    : "translate-y-0"
                  : "translate-y-0"
              }`}
              style={{
                zIndex: 2,
                transform: isAnimating
                  ? scrollDirection === "down"
                    ? "translateY(0)"
                    : "translateY(0)"
                  : "translateY(0)",
                ...(isAnimating && {
                  animation:
                    scrollDirection === "down"
                      ? "slideFromBottom 0.3s ease-out" // ✅ NEW
                      : "slideFromTop 0.3s ease-out", // ✅ NEW
                }),
              }}
            >
              {renderFeedbackContent(selectedFeedback || null)}
            </div>
          </div>
        </div>
      </div>
      {/* Submit Feedback Confirmation Popup */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mt-2 mb-2">
                <h3
                  className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Ready to share?
                </h3>
              </div>
              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Description */}
              <p
                className="text-[#070707] text-sm 3xl:text-[16px] mb-6"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Once shared, you won’t be able to add new feedback or make any
                changes in this project.
              </p>
              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />
              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleSubmitCancel}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleSubmitConfirm}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Header */}
              <div className="flex items-center justify-between mt-2 mb-2">
                <h3
                  className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Confirm feedback deletion
                </h3>
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-6" />

              {/* Description */}
              <p
                className="text-[#2A354799] text-sm 3xl:text-[16px] mb-6"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Deleting this feedback will permanently remove it and all
                related information from the database. This action cannot be
                undone.
              </p>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-4" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleDeleteCancel}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleDeleteConfirm}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Feedback Popup */}
      <ShareFeedbackPopup
        isOpen={isSharePopupOpen}
        onClose={() => setIsSharePopupOpen(false)}
        title={`Share "${feedbackName || "feedback"}" with developers`}
        totalFeedback={totalFeedbacks}
        onShare={handleShareEmails}
        onCancel={() => setIsSharePopupOpen(false)}
        cardId={cardId ?? undefined}
      />

      {/*  Screenshot Edit Preview */}
      {showEditScreenshotPreview && editingScreenshotData && (
        <ScreenshotPreviewComponent
          preview={{
            dataUrl: editingScreenshotData.dataUrl,
            area: { startX: 0, startY: 0, endX: 0, endY: 0 },
            actualWidth: 0,
            actualHeight: 0,
          }}
          isLoading={false}
          onClose={() => {
            setShowEditScreenshotPreview(false);
            setEditingScreenshotData(null);
          }}
          onDownload={() => {
            // Optional: Add download functionality
          }}
          onSubmit={handleEditScreenshotSubmit}
          uniqueFeedbackId={editingScreenshotData.feedbackId}
          isEditMode={true}
          existingFeedback={editingScreenshotData.feedback}
          existingAudioUrl={editingScreenshotData.audioUrl}
          existingAudioBlob={editingScreenshotData.audioBlob}
          existingAudioFilename={editingScreenshotData.audioFilename}
          isFromViewListFeedback={true} // ✅ ENSURE THIS IS SET TO TRUE
        />
      )}

      {/*  Recording Edit Preview */}
      {showEditRecordingPreview && editingRecordingData && (
        <ScreenRecordingPreview
          preview={{
            dataUrl: editingRecordingData.dataUrl,
            blob: editingRecordingData.recordingBlob || new Blob(),
            duration: editingRecordingData.duration,
            timestamp: Date.now(),
          }}
          onClose={() => {
            setShowEditRecordingPreview(false);
            setEditingRecordingData(null);
          }}
          onDownload={() => {
            // Optional: Add download functionality
          }}
          onSubmit={handleEditRecordingSubmit}
          uniqueFeedbackId={editingRecordingData.feedbackId}
          isEditMode={true}
          existingFeedback={editingRecordingData.feedback}
          existingRecordingBlob={editingRecordingData.recordingBlob}
          existingDataUrl={editingRecordingData.dataUrl}
          existingThumbnailUrl={editingRecordingData.thumbnailUrl}
          isFromViewListFeedback={true} // ✅ ENSURE THIS IS SET TO TRUE
        />
      )}

      {/*  Edit Feedback Popup */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-[10px] shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xl xl:max-w-xl 2xl:max-w-2xl 4xl:max-w-3xl w-full mx-4">
            <div className="px-4 py-4">
              {/* Text Editor */}
              <div className="mb-3" style={{ height: "300px" }}>
                <TextEditor
                  value={editedFeedbackText}
                  onChange={setEditedFeedbackText}
                  disabled={isSavingEdit || isSubmittedMode}
                  placeholder="Type your feedback here..."
                />
              </div>

              {/* Separator */}
              <div className="border-t border-gray-200 mb-3" />

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="cancel"
                   size="lg"
                  className="w-full sm:w-auto text-sm"
                  onClick={handleCancelEdit}
                  disabled={isSavingEdit}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {isSubmittedMode ? "Close" : "Cancel"}
                </Button>
                {!isSubmittedMode && (
                  <Button
                    variant="primary"
                     size="lg"
                  className="w-full sm:w-auto text-sm"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                    onClick={handleSaveEdit}
                    disabled={!editedFeedbackText.trim()}
                  >
                    {isSavingEdit ? (
                      <span
                        className="inline-block relative"
                        style={{ width: 60, height: 20 }}
                      >
                        <Lottie
                          animationData={DotLoader}
                          loop={true}
                          style={{
                            width: 120,
                            height: 48,
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                          }}
                        />
                      </span>
                    ) : (
                      "Update"
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
      .line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
     /*  Toast Animation */
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .animate-slide-in {
    animation: slideIn 0.3s ease-out;
  }
  
  
  /* Real scroll animations - like Instagram Reels */
  @keyframes slideFromBottom {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  @keyframes slideFromTop {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  /* Smooth transition for all transform changes */
.transition-transform {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);  
}
   
.duration-300 {
  transition-duration: 300ms;  
}
  /* 🆕 NEW: Tooltip animation for developer names */
@keyframes tooltip-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.animate-tooltip {
  animation: tooltip-in 0.15s ease-out;
}
  

`}</style>
    </div>
  );
};

export default ViewListFeedback;
